import coachAvatarImg from '@/assets/avatars/coach-avatar.jpg';
import { useDeviceIdContext } from '@/components/device-id-provider';
import { Button } from '@/components/ui/button';
import { SessionEndButton } from '@/components/ui/session-end-button';
import { SessionSummary } from '@/components/ui/session-summary';
import { useHeyGenToken } from '@/hooks/useHeyGenToken';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useSessionSummary } from '@/hooks/useSessionSummary';
import { cn } from '@/lib/utils';
import { ELEVEN_LABS_OCTAVE_AGENT_ID, HEYGEN_AVATARS } from '@/utils/constants';
import { useAppApproach } from '@/contexts/AppModuleContext';
import api from '@/utils/ky';
import { Conversation } from '@elevenlabs/client';
import StreamingAvatar, {
  AvatarQuality,
  StreamingEvents,
  TaskMode,
  TaskType,
} from '@heygen/streaming-avatar';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft,
  Info,
  Mic,
  MicOff,
  Phone,
  Settings,
  Shield,
  Video,
  VideoOff,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MediaPreview } from './MediaPreview';
import { SessionStatusIndicator } from '@/components/ui/session-status-indicator';

// Default user name for coaching sessions
const DEFAULT_APP_USER_NAME = 'Mir';

// Environment flags
const DISABLE_HEYGEN = import.meta.env.VITE_DISABLE_HEYGEN === 'true';
const DEBUG_USER_VIDEO = import.meta.env.VITE_DEBUG_USER_VIDEO === 'true';
const ENABLE_SPEAKING_DETECTION = import.meta.env.VITE_ENABLE_SPEAKING_DETECTION === 'true';
const SHOW_TOASTS = import.meta.env.VITE_SHOW_TOASTS === 'true';

// Session initialization states for robust startup
enum SessionState {
  INITIALIZING = 'initializing',
  AVATAR_READY = 'avatar_ready',
  AVATAR_SPEAKING_FIRST = 'avatar_speaking_first',
  VOICE_READY = 'voice_ready',
  FULLY_ACTIVE = 'fully_active',
  ERROR = 'error',
  RETRY = 'retry'
}

// Voice chat configuration for natural conversation flow
const VOICE_CHAT_CONFIG = {
  // Silence detection: How long to wait during pauses before AI responds
  silenceDurationMs: 2000,      // 2 seconds - allows for natural pauses

  // Voice activity detection sensitivity (0-1)
  // Higher = less sensitive = needs clearer speech
  vadSensitivity: 0.7,          // Prevents false triggers from background noise

  // End of speech delay before processing
  endOfSpeechDelayMs: 1500,     // 1.5 seconds - prevents cutting off mid-thought

  // Interruption settings
  allowInterruption: true,      // Allow user to interrupt AI
  interruptionDelayMs: 300,     // Brief delay before interrupting
};

// Session initialization timing configuration
const SESSION_INIT_CONFIG = {
  avatarReadyTimeout: 10000,    // 10s max wait for avatar
  firstMessageDelay: 1500,      // 1.5s delay before first message
  voiceChatDelay: 500,          // 500ms after first message ends
  retryDelay: 2000,             // 2s between retries
  maxRetries: 3,                // Max retry attempts
};

interface MediaCallProps {
  mode: 'voice' | 'video';
  moduleId: string;
}

// Define interface for API response
interface InstructionsResponse {
  instructions: string;
  firstMessage: string;
  moduleId?: string;
  moduleTitle?: string;
  coachName?: string;
  avatarId?: string;
  voiceId?: string;
  avatarColor?: string;
  mode?: 'text' | 'voice' | 'video'; // Mode of interaction
  userName?: string; // User's name for personalized coaching
  sessionId?: string; // Backend-generated session ID
}

// Define module configuration structure
interface ModuleConfig {
  title: string;
  coachName: string;
  avatarId: string;
  voiceId: string;
  avatarColor: string;
  instructions: string;
  firstMessage?: string;
  interactionMode?: 'text' | 'voice' | 'video'; // Store current interaction mode
  userName?: string; // User's name for personalized coaching
}

export function MediaCall({ mode, moduleId }: MediaCallProps) {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLDivElement>(null);

  // Get device ID for API requests
  const { deviceId } = useDeviceIdContext();

  // Get current coaching approach
  const { currentApproach, approachConfig } = useAppApproach();

  // Use the token hooks to access the pre-fetched resources or generate new ones if needed
  const {
    token: preloadedToken,
    generateToken,
  } = useHeyGenToken();

  // Use session summary hook for end session functionality
  const {
    endSession,
    clearSummary,
    isLoading: isSummaryLoading,
    error: summaryError,
    summaryData,
  } = useSessionSummary();


  // We'll initialize 11Labs directly instead of using a preloaded session

  // Default module data - will be replaced with API data
  const defaultModuleData: ModuleConfig = {
    title: 'Coaching Session',
    coachName: 'Maya',
    avatarId: HEYGEN_AVATARS.DEFAULT, // Using the June avatar as default
    voiceId: ELEVEN_LABS_OCTAVE_AGENT_ID,
    avatarColor: 'bg-purple-500',
    interactionMode: mode, // Set default interaction mode from component props
    userName: DEFAULT_APP_USER_NAME, // Default user name
    instructions: `You are a helpful AI coach focused on personal development.
Your role is to help users explore their challenges and create actionable solutions.
Ask thoughtful questions to understand the user's needs.
Provide practical advice and techniques when appropriate.
Be supportive and empathetic in your responses.
Keep your answers concise and focused on the user's goals.`,
  };

  // State
  const [isConnected, setIsConnected] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [callDuration, setCallDuration] = useState(0);
  const [isAISpeaking, setIsAISpeaking] = useState(false);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [lastUserMessage, setLastUserMessage] = useState<string | null>(null);
  const [loadingState, setLoadingState] = useState('connecting');
  const [error, setError] = useState<string | null>(null);
  const [showSummary, setShowSummary] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [userStream, setUserStream] = useState<MediaStream | null>(null);
  const [selectedVideoInput, setSelectedVideoInput] = useState<string>('');

  // Will be set from backend response
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionEnded, setSessionEnded] = useState(false);

  // Module state with dynamic updates
  const [moduleInfo, setModuleInfo] = useState<ModuleConfig>(() => {
    // For agile approach, try to find the module title
    if (currentApproach === 'agile') {
      const agileModule = approachConfig.growthAreas.find(area => area.id === moduleId);
      if (agileModule) {
        return {
          ...defaultModuleData,
          title: agileModule.title,
        };
      }
    }
    return { ...defaultModuleData };
  });

  // Session initialization state management
  const [sessionState, setSessionState] = useState<SessionState>(SessionState.INITIALIZING);
  const [sessionRetryCount, setSessionRetryCount] = useState(0);
  const [sessionStatusMessage, setSessionStatusMessage] = useState('Setting up your coaching session...');

  // Turn-taking management
  const [isMicrophoneBlocked, setIsMicrophoneBlocked] = useState(false);
  const [turnTakingMessage, setTurnTakingMessage] = useState<string | null>(null);

  // Early input handling
  const earlyInputQueue = useRef<string[]>([]);
  const isVoiceChatReady = useRef(false);
  const firstMessageSpoken = useRef(false);

  // Turn-taking recovery timeout
  const turnTakingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Refs
  const durationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);
  const conversationRef = useRef<Conversation | null>(null);
  const streamingAvatarRef = useRef<any>(null);
  const videoElementRef = useRef<HTMLVideoElement | null>(null);
  const userVideoRef = useRef<HTMLVideoElement | null>(null);
  const streamAssignedRef = useRef<boolean>(false);
  const visibilityChangeListenerRef = useRef<(() => void) | null>(null);
  const beforeUnloadListenerRef = useRef<
    ((e: BeforeUnloadEvent) => void) | null
  >(null);
  const eventListenersRef = useRef<{ type: string; handler: any }[]>([]);

  // Store session messages for summary
  const messagesRef = useRef<
    { role: 'user' | 'coach' | 'system'; content: string; timestamp: string }[]
  >([]);

  // Track current messages being built from events
  const currentAvatarMessageRef = useRef<string>('');
  const currentUserMessageRef = useRef<string>('');

  // Track if we're currently building messages to avoid duplicates
  const isAvatarSpeakingRef = useRef<boolean>(false);
  const isUserSpeakingRef = useRef<boolean>(false);

  // Timeout references for message finalization
  const avatarMessageTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const userMessageTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Simple helper to add messages with logging
  const addMessageToTranscript = (role: 'user' | 'coach' | 'system', content: string, source: string) => {
    // 🔍 DEBUG: Log every message construction attempt
    console.log(`[MediaCall:Transcript:DEBUG] 🔍 Message construction attempt:`, {
      role,
      content,
      contentLength: content?.length || 0,
      contentType: typeof content,
      source,
      isEmpty: !content || content.trim() === '',
      timestamp: new Date().toISOString()
    });

    if (!content || content.trim() === '') {
      console.log(`[MediaCall:Transcript] ❌ Skipping empty message from ${source} - no content to add`);
      return;
    }

    // Auto-correct role based on content heuristics to prevent role confusion
    let correctedRole = role;
    const lowerContent = content.toLowerCase();

    // If content mentions being Maya/coach but role is user, correct it
    if (role === 'user' && (lowerContent.includes("i'm maya") || lowerContent.includes("your coach"))) {
      console.warn(`[MediaCall:Transcript] ⚠️ Role mismatch detected - content suggests coach but role is user. Correcting to coach.`);
      correctedRole = 'coach';
    }

    // If content is very coaching-like but attributed to user, correct it
    if (role === 'user' && (lowerContent.includes("what would you like to") || lowerContent.includes("i'm here to guide"))) {
      console.warn(`[MediaCall:Transcript] ⚠️ Role mismatch detected - coaching language but role is user. Correcting to coach.`);
      correctedRole = 'coach';
    }

    const newMessage = {
      role: correctedRole,
      content: content.trim(),
      timestamp: new Date().toISOString(),
    };

    // 🔍 DEBUG: Log the complete message object before adding
    console.log(`[MediaCall:Transcript:DEBUG] 📝 Complete message object being added:`, {
      messageObject: newMessage,
      originalRole: role,
      correctedRole,
      fullContent: content,
      trimmedContent: content.trim(),
      source,
      messageCountBefore: messagesRef.current.length,
    });

    messagesRef.current.push(newMessage);

    // 🔍 DEBUG: Log final state after adding
    console.log(`[MediaCall:Transcript:DEBUG] ✅ Message successfully added to transcript:`, {
      messageIndex: messagesRef.current.length - 1,
      totalMessages: messagesRef.current.length,
      addedMessage: newMessage,
      source,
      allMessages: messagesRef.current.map((msg, index) => ({
        index,
        role: msg.role,
        contentPreview: msg.content.substring(0, 50) + (msg.content.length > 50 ? '...' : ''),
        timestamp: msg.timestamp
      }))
    });

    console.log(`[MediaCall:Transcript] ✅ Added ${correctedRole} message from ${source}:`, {
      originalRole: role,
      correctedRole,
      content: content.substring(0, 100) + (content.length > 100 ? '...' : ''),
      source,
      messageCount: messagesRef.current.length,
      timestamp: newMessage.timestamp
    });
  };

  // Helper to finalize and add a complete message to transcript
  const finalizeMessage = (role: 'user' | 'coach', content: string, source: string) => {
    // 🔍 DEBUG: Log every finalization attempt
    console.log(`[MediaCall:Transcript:DEBUG] 🏁 Message finalization attempt:`, {
      role,
      content,
      contentLength: content?.length || 0,
      contentType: typeof content,
      source,
      hasContent: !!(content && content.trim()),
      timestamp: new Date().toISOString()
    });

    if (content && content.trim()) {
      console.log(`[MediaCall:Transcript:DEBUG] ➡️ Calling addMessageToTranscript from ${source}:`, {
        role,
        content: content.trim(),
        source
      });
      addMessageToTranscript(role, content.trim(), source);
    } else {
      console.warn(`[MediaCall:Transcript] ❌ Attempted to finalize empty message from ${source}:`, {
        role,
        content,
        contentLength: content?.length || 0,
        contentType: typeof content,
        hasContent: !!(content && content.trim())
      });
    }
  };

  // Debug helper to log all events
  const logAllEvents = (eventName: string, event: any) => {
    console.log(`[MediaCall:HeyGen:AllEvents] 📡 ${eventName}:`, {
      timestamp: new Date().toISOString(),
      eventName,
      event,
      eventKeys: event ? Object.keys(event) : [],
      eventType: typeof event,
      hasDetail: !!event?.detail,
      detailKeys: event?.detail ? Object.keys(event.detail) : [],
      hasMessage: !!(event?.message || event?.text || event?.detail?.message),
      messageContent: event?.detail?.message || event?.message || event?.text || 'No message found'
    });
  };

  // Schedule message finalization with timeout
  const scheduleMessageFinalization = (role: 'user' | 'coach', delay: number = 2000) => {
    const timeoutRef = role === 'user' ? userMessageTimeoutRef : avatarMessageTimeoutRef;
    const messageRef = role === 'user' ? currentUserMessageRef : currentAvatarMessageRef;
    const speakingRef = role === 'user' ? isUserSpeakingRef : isAvatarSpeakingRef;

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Schedule finalization
    timeoutRef.current = setTimeout(() => {
      if (speakingRef.current && messageRef.current && messageRef.current.trim()) {
        console.log(`[MediaCall:Transcript] ⏰ Timeout finalization for ${role}:`, {
          messageLength: messageRef.current.length,
          content: messageRef.current,
          hasContent: !!messageRef.current.trim()
        });
        finalizeMessage(role, messageRef.current, `${role.toUpperCase()}_TIMEOUT_FINALIZATION`);
        messageRef.current = '';
        speakingRef.current = false;
      } else {
        console.log(`[MediaCall:Transcript] ⏰ Skipped timeout finalization for ${role} - no content:`, {
          speakingState: speakingRef.current,
          messageLength: messageRef.current?.length || 0,
          content: messageRef.current || 'empty'
        });
      }
      timeoutRef.current = null;
    }, delay);
  };

  // Comprehensive stream cleanup utility
  const cleanupAllStreams = () => {
    console.log('[MediaCall] Running comprehensive stream cleanup');

    // Clean up turn-taking timeout
    if (turnTakingTimeoutRef.current) {
      console.log('[MediaCall] Clearing turn-taking timeout');
      clearTimeout(turnTakingTimeoutRef.current);
      turnTakingTimeoutRef.current = null;
    }

    // Clean up user media stream
    if (userStream) {
      console.log('[MediaCall] Stopping user media stream tracks');
      userStream.getTracks().forEach(track => {
        console.log(`[MediaCall] Stopping track: ${track.kind}, enabled: ${track.enabled}, readyState: ${track.readyState}`);
        track.stop();
      });
      setUserStream(null);
    }

    // Clean up any video element streams
    if (userVideoRef.current && userVideoRef.current.srcObject) {
      console.log('[MediaCall] Cleaning up video element srcObject');
      const stream = userVideoRef.current.srcObject as MediaStream;
      if (stream && stream.getTracks) {
        stream.getTracks().forEach(track => {
          console.log(`[MediaCall] Stopping video element track: ${track.kind}`);
          track.stop();
        });
      }
      userVideoRef.current.srcObject = null;
    }


    // Force garbage collection hint for streams
    streamAssignedRef.current = false;
    console.log('[MediaCall] Stream cleanup completed');
  };

  // Fetch module data from backend with mode parameter
  const fetchModuleData = async () => {
    try {
      // Map component mode prop to API expected mode parameter
      const apiMode =
        mode === 'voice' ? 'voice' : mode === 'video' ? 'video' : 'text';

      console.log(
        `[MediaCall] Fetching module data with mode=${apiMode} and userName=${DEFAULT_APP_USER_NAME}`,
      );

      // Ensure we have a device ID before making the request
      if (!deviceId) {
        console.error('[MediaCall] Device ID is required but not available, using fallback');
        throw new Error('Device ID not available');
      }

      console.log('[MediaCall] Making instructions request with device ID:', deviceId);

      // Make API call with mode parameter, userName, and explicit device ID in headers
      const headers: Record<string, string> = {
        'x-device-id': deviceId,
        'Content-Type': 'application/json',
      };

      const response = await api
        .get(
          `api/app/modules/${moduleId}/instructions?mode=${apiMode}&userName=${DEFAULT_APP_USER_NAME}`,
          { headers },
        )
        .json<InstructionsResponse>();

      // Log the response mode for debugging
      console.log(
        `[MediaCall] Received instructions with mode: ${
          response.mode || apiMode
        }`,
      );

      console.log('Response from backend:', response);

      // Store sessionId from backend if provided
      if (response.sessionId) {
        console.log('Received sessionId from backend:', response.sessionId);
        setSessionId(response.sessionId);
      } else {
        console.warn('No sessionId provided by backend, session end may fail');
      }

      // Update module info based on response
      const updatedModuleInfo: ModuleConfig = { ...defaultModuleData };

      if (response.instructions) {
        updatedModuleInfo.instructions = response.instructions;
      }

      if (response.moduleTitle) {
        updatedModuleInfo.title = response.moduleTitle;
      }

      if (response.coachName) {
        updatedModuleInfo.coachName = response.coachName;
      }

      if (response.avatarId) {
        updatedModuleInfo.avatarId = response.avatarId;
      }

      if (response.voiceId) {
        updatedModuleInfo.voiceId = response.voiceId;
      }

      if (response.avatarColor) {
        updatedModuleInfo.avatarColor = response.avatarColor;
      }

      // Store the userName from response or use default
      if (response.userName) {
        updatedModuleInfo.userName = response.userName;
      } else {
        updatedModuleInfo.userName = DEFAULT_APP_USER_NAME;
      }

      // Store the interaction mode from API response or use component mode
      updatedModuleInfo.interactionMode = response.mode || (apiMode as any);
      console.log(
        `[MediaCall] Using interaction mode: ${updatedModuleInfo.interactionMode}`,
      );

      // Include firstMessage in moduleInfo for use in both voice and video modes
      if (response.firstMessage) {
        // Use the provided first message but ensure it's coach-centered
        console.log(
          '[MediaCall] Using first message from backend module data:',
          response.firstMessage,
        );
        updatedModuleInfo.firstMessage = response.firstMessage;
      } else {
        // Generate a module-specific default message based on the module ID
        const generateModuleSpecificMessage = (moduleId: string) => {
          // Extract readable module name from ID
          const readableModuleName = moduleId
            .split('-')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

          // Get user's name for personalized messages
          const userName = updatedModuleInfo.userName || DEFAULT_APP_USER_NAME;

          // Map of module-specific messages with coaching language and personalization
          const moduleMessages: Record<string, string> = {
            'general-coaching': `Hi ${userName}, I'm ${updatedModuleInfo.coachName}. I'm here to help you explore any challenges or opportunities you'd like to discuss today. What's on your mind?`,
            'staying-centered': `Welcome ${userName}! I'm ${updatedModuleInfo.coachName}. Let's explore ways to stay centered during tense situations. What specific scenarios are you finding challenging?`,
            'breaking-free': `Hi ${userName}, I'm ${updatedModuleInfo.coachName}. Today we'll explore how to break free from limiting beliefs. What beliefs might be holding you back?`,
          };

          // Return module-specific message or fallback to default
          return (
            moduleMessages[moduleId] ||
            `Hi ${userName}, I'm ${updatedModuleInfo.coachName}. I'm here to help you explore ${readableModuleName}. What would you like to focus on today?`
          );
        };

        // Generate default message based on moduleId
        const defaultMessage = generateModuleSpecificMessage(moduleId);
        updatedModuleInfo.firstMessage = defaultMessage;
      }

      // Update state with new module info
      return new Promise<ModuleConfig>((resolve) => {
        setModuleInfo(updatedModuleInfo);
        // Return the updated moduleInfo for direct use
        resolve(updatedModuleInfo);
      });
    } catch (err) {
      console.log('Error fetching module data:', err);
      // Add default firstMessage if we had to use fallback data
      const generateFallbackMessage = (moduleId: string) => {
        // Extract readable module name from ID
        const readableModuleName = moduleId
          .split('-')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');

        // Get user's name for personalized messages
        const userName = DEFAULT_APP_USER_NAME;

        // Shorter, more natural coach messages with personalization
        const moduleMessages: Record<string, string> = {
          'general-coaching': `Hi ${userName}. What would you like to discuss today?`,
          'staying-centered': `Welcome ${userName}. I'd love to help you explore ways to stay centered in challenging situations.`,
          'breaking-free': `Hi ${userName}, I'm here to help you identify and move beyond limiting beliefs. Where should we start?`,
        };

        return (
          moduleMessages[moduleId] ||
          `Hi ${userName}, I'm here to help with ${readableModuleName}. What would you like to focus on?`
        );
      };

      const defaultMessage = generateFallbackMessage(moduleId);

      const updatedModuleInfo: ModuleConfig = {
        ...moduleInfo,
        firstMessage: defaultMessage,
        interactionMode: mode, // Fallback to component mode if API call fails
        userName: DEFAULT_APP_USER_NAME, // Add default user name in fallback
      };

      // First message will be captured via HeyGen events when avatar speaks

      // Update state with fallback module info
      return new Promise<ModuleConfig>((resolve) => {
        setModuleInfo(updatedModuleInfo);
        // Return the updated moduleInfo for direct use
        resolve(updatedModuleInfo);
      });
    }
  };

  // Connection state tracking for proper cleanup
  const [isInitializing, setIsInitializing] = useState(false);
  const originalErrorHandlerRef = useRef<OnErrorEventHandler | null>(null);

  // Manual retry handler
  const handleManualRetry = () => {
    console.log('[MediaCall] Manual retry requested');

    // Reset state
    setSessionState(SessionState.INITIALIZING);
    setSessionStatusMessage('Reconnecting to your coach...');
    setSessionRetryCount(0);
    setError(null);
    firstMessageSpoken.current = false;
    isVoiceChatReady.current = false;

    // Clear any early input queue
    earlyInputQueue.current = [];

    // Re-initialize
    if (mode === 'video') {
      initializeHeyGen(0);
    } else {
      initialize11Labs();
    }
  };

  // Initialize user media based on preview settings
  const initializeUserMedia = async (config: {
    audioEnabled: boolean;
    videoEnabled: boolean;
    selectedAudioInput: string;
    selectedVideoInput: string;
    selectedAudioOutput: string;
  }) => {
    setSelectedVideoInput(config.selectedVideoInput);
    setIsMuted(!config.audioEnabled);
    setIsVideoOn(config.videoEnabled);

    // Get user video stream if video is enabled
    if (mode === 'video' && config.videoEnabled) {
      try {
        console.log('[MediaCall] Requesting camera access...');
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: config.selectedVideoInput ? { exact: config.selectedVideoInput } : undefined,
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
          audio: false // Audio is handled by 11Labs/HeyGen
        });

        console.log('[MediaCall] Camera stream obtained successfully');
        streamAssignedRef.current = false;
        setUserStream(stream);
      } catch (err) {
        console.error('[MediaCall] Error accessing camera:', err);
        setIsVideoOn(false);
      }
    }
  };

  // Handle preview completion
  const handlePreviewComplete = async (config: {
    audioEnabled: boolean;
    videoEnabled: boolean;
    selectedAudioInput: string;
    selectedVideoInput: string;
    selectedAudioOutput: string;
  }) => {
    console.log('[MediaCall] Preview completed, initializing media...');
    setShowPreview(false);
    await initializeUserMedia(config);
  };

  // Update user video element when stream changes
  useEffect(() => {
    if (userVideoRef.current && userStream && !streamAssignedRef.current) {
      userVideoRef.current.srcObject = userStream;
      streamAssignedRef.current = true;
      userVideoRef.current.play().catch(() => {});
      console.log('[MediaCall] User video stream assigned to element');
    }
  }, [userStream]);


  useEffect(() => {
    if (!showPreview && deviceId) {
      console.log(`[MediaCall] Initializing ${mode} call mode`);
      let isMounted = true;
      setIsInitializing(true);

    // Store original error handler
    originalErrorHandlerRef.current = window.onerror;

    // First fetch data from backend, then initialize based on the mode
    const initializeCall = async () => {
      try {
        console.log(`[MediaCall] Fetching module data for ${moduleId}`);
        const backendResponse = await fetchModuleData();

        if (!isMounted) {
          console.log(
            '[MediaCall] Component unmounted during initialization, aborting',
          );
          return;
        }

        // Ensure any existing connections are properly closed before starting new ones
        if (conversationRef.current) {
          console.log(
            '[MediaCall] Cleaning up existing conversation before initializing new one',
          );
          try {
            await conversationRef.current.endSession();
            conversationRef.current = null;
          } catch (err) {
            console.warn(
              '[MediaCall] Error closing existing conversation:',
              err,
            );
          }
        }

        if (streamingAvatarRef.current) {
          console.log(
            '[MediaCall] Cleaning up existing streaming avatar before initializing new one',
          );
          try {
            // Use our comprehensive cleanup instead of just disconnect
            cleanupHeyGenAvatar();
          } catch (err) {
            console.warn(
              '[MediaCall] Error cleaning up existing streaming avatar:',
              err,
            );
          }
        }

        // Wait for state update to fully propagate before initializing
        console.log(
          `[MediaCall] Waiting for moduleInfo state update before initializing ${mode} connection`,
        );

        // Small delay to ensure state updates are fully processed
        await new Promise((resolve) => setTimeout(resolve, 10));

        // Now initialize the appropriate connection type with the latest moduleInfo
        console.log(`[MediaCall] Now initializing ${mode} connection`);
        if (mode === 'video') {
          if (DISABLE_HEYGEN) {
            console.log('[MediaCall] HeyGen disabled via environment variable, using mock avatar');
            // Set up mock state for testing user video without HeyGen costs
            setLoadingState('ready');
            setIsConnected(true);

            // Start mock AI speaking occasionally for testing
            setTimeout(() => {
              setIsAISpeaking(true);
              setTimeout(() => setIsAISpeaking(false), 3000);
            }, 2000);
          } else {
            await initializeHeyGen();
          }
        } else {
          // Pass the freshly fetched module data to initialize11Labs
          await initialize11Labs(backendResponse);
        }

        setIsInitializing(false);
      } catch (err) {
        console.error(`[MediaCall] Error during initialization:`, err);
        setError('Failed to establish connection. Please try again.');
        setLoadingState('error');
        setIsInitializing(false);
      }
    };

    initializeCall();

    // Start duration timer once connected
    durationTimerRef.current = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);

    // Handle browser back button navigation
    const handlePopState = () => {
      console.log('[MediaCall] Browser back button detected');
      // The cleanup will happen in the unmount effect below
    };

    window.addEventListener('popstate', handlePopState);

    // Component unmount cleanup
    return () => {
      console.log(
        `[MediaCall] Component unmounting, cleaning up ${mode} connection`,
      );
      isMounted = false;

      // Remove event listener
      window.removeEventListener('popstate', handlePopState);

      // Cleanup timers
      if (durationTimerRef.current) {
        clearInterval(durationTimerRef.current);
        durationTimerRef.current = null;
      }

      // Cleanup message finalization timeouts
      if (avatarMessageTimeoutRef.current) {
        clearTimeout(avatarMessageTimeoutRef.current);
        avatarMessageTimeoutRef.current = null;
      }
      if (userMessageTimeoutRef.current) {
        clearTimeout(userMessageTimeoutRef.current);
        userMessageTimeoutRef.current = null;
      }

      // Gracefully close any active connections with proper cleanup
      if (mode === 'video' && streamingAvatarRef.current) {
        console.log(
          '[MediaCall] Component unmount - running comprehensive HeyGen cleanup',
        );
        try {
          // Use our comprehensive cleanup function with proper session termination
          cleanupHeyGenAvatar();
        } catch (err) {
          console.warn(
            '[MediaCall] Error during comprehensive cleanup on unmount:',
            err,
          );
        }
      }

      if (conversationRef.current) {
        console.log('[MediaCall] Ending 11Labs conversation session');
        try {
          // Run the custom cleanup function if available
          if (
            (conversationRef.current as any).cleanup &&
            typeof (conversationRef.current as any).cleanup === 'function'
          ) {
            console.log('[MediaCall] Running custom cleanup function');
            (conversationRef.current as any).cleanup();
          }

          // End the session safely
          console.log('[MediaCall] Calling endSession');
          conversationRef.current.endSession();
          conversationRef.current = null;
        } catch (err) {
          console.warn('[MediaCall] Error ending conversation session:', err);
        }
      }

      // Restore original error handler if we modified it
      if (
        window.onerror !== originalErrorHandlerRef.current &&
        originalErrorHandlerRef.current !== null
      ) {
        console.log('[MediaCall] Restoring original error handler');
        window.onerror = originalErrorHandlerRef.current;
      }

      // Clear any remaining page lifecycle event listeners
      if (visibilityChangeListenerRef.current) {
        console.log(
          '[MediaCall] Removing visibility change listener on unmount',
        );
        document.removeEventListener(
          'visibilitychange',
          visibilityChangeListenerRef.current,
        );
        visibilityChangeListenerRef.current = null;
      }

      if (beforeUnloadListenerRef.current) {
        console.log('[MediaCall] Removing beforeunload listener on unmount');
        window.removeEventListener(
          'beforeunload',
          beforeUnloadListenerRef.current,
        );
        beforeUnloadListenerRef.current = null;
      }

      setIsConnected(false);

      // Run comprehensive stream cleanup
      cleanupAllStreams();
    };
    }
  }, [mode, moduleId, showPreview, deviceId]);

  const initialize11Labs = async (freshModuleInfo?: ModuleConfig) => {
    console.log('[MediaCall:11Labs] Initializing 11Labs conversation');
    try {
      // Use the freshest module info - either passed in directly or from state
      const currentModuleInfo = freshModuleInfo || moduleInfo;
      console.log('[MediaCall:11Labs] Using module info:', currentModuleInfo);

      // Format the instructions to properly set the AI coach persona
      // Include mode-specific instructions based on interactionMode with clarity about roles
      const formattedInstructions = `
You are an AI coach named ${
        currentModuleInfo.coachName
      } providing guidance on ${currentModuleInfo.title}.
The person you are coaching is named ${
        currentModuleInfo.userName || DEFAULT_APP_USER_NAME
      }.
${currentModuleInfo.instructions}

QUANTUM LEADERSHIP & ONTOLOGICAL COACHING PRINCIPLES:
1. Focus on the client's way of being, not just their actions
2. Observe language patterns as pathways to deeper understanding
3. Honor the client's unique perspective and lived experience
4. Create a safe space for authentic exploration
5. Facilitate shifts in perception that enable new possibilities

CRITICAL ROLE CLARITY:
1. YOU ARE THE COACH - The person interacting with you is the CLIENT
2. Do NOT introduce yourself or speak as if you are the one seeking coaching
3. Always maintain your role as the coach providing guidance and support
4. The user is coming to you for coaching - they are never the coach
5. Assume the user has zero coaching training - you are the expert

IMPORTANT GUIDELINES:
1. You are the coach, and the user is seeking your guidance and expertise
2. Speak directly to the user as if you are having a real coaching conversation
3. Ask thoughtful questions to understand the user's situation
4. Provide practical advice and actionable suggestions
5. Be empathetic, supportive, and positive
6. Keep your responses concise and focused
7. Never confuse roles - you are always the coach, never the client


${
  currentModuleInfo.interactionMode === 'voice'
    ? `
VOICE CALL SPECIFIC GUIDELINES:
1. Remember you're on a VOICE CALL - no visual cues available
2. Keep responses brief and conversational (2-3 sentences at most)
3. Use your tone to convey warmth and understanding
4. Ask more clarifying questions since you can't see body language
5. Use verbal acknowledgments: "I hear you", "That makes sense", "Mm-hmm"
6. Avoid lists or structured formats - speak naturally
7. Check in more frequently: "How does that feel?" or "Does that resonate?"
8. Start warmly: "Hi [Name], it's great to connect with you. What would you like to explore today?"
`
    : ''
}

${
  currentModuleInfo.interactionMode === 'text'
    ? `
TEXT CHAT SPECIFIC GUIDELINES:
1. Remember this is TEXT CHAT - you can use formatting effectively
2. Keep responses concise but can be slightly longer than voice/video
3. Use paragraph breaks for clarity
4. You MAY use bullet points or numbered lists when helpful
5. Ask one question at a time and wait for response
6. Use emojis sparingly and professionally (e.g., 🤔 for thinking)
7. Start with: "Hi [Name], I'm here to support you. What's on your mind today?"
`
    : ''
}
`;
      console.log(
        '[MediaCall:11Labs] Using formatted instructions with mode-specific guidance',
      );
      const instructions = formattedInstructions;

      // Use the firstMessage from the backend module data (already validated in fetchModuleData)
      // This ensures the moduleInfo.firstMessage is already properly formatted and from coach perspective
      console.log(
        '[MediaCall:11Labs] Checking for backend-provided first message...',
      );
      console.log('[MediaCall:11Labs] Current moduleInfo:', currentModuleInfo);

      let firstMessage = '';
      if (currentModuleInfo && currentModuleInfo.firstMessage) {
        console.log(
          '[MediaCall:11Labs] Using first message from backend module data:',
          currentModuleInfo.firstMessage,
        );
        firstMessage = currentModuleInfo.firstMessage;
      } else {
        console.log(
          '[MediaCall:11Labs] No first message from backend, using fallback',
        );
        const userName = currentModuleInfo.userName || DEFAULT_APP_USER_NAME;
        firstMessage = `Hello ${userName}, I'm ${currentModuleInfo.coachName}, your coach for today's session on ${currentModuleInfo.title}. I'm here to guide and support you. What would you like to focus on in our conversation today?`;
      }

      console.log(
        '[MediaCall:11Labs] Using agent ID:',
        ELEVEN_LABS_OCTAVE_AGENT_ID,
      );
      console.log(
        '[MediaCall:11Labs] Using voice ID:',
        currentModuleInfo.voiceId,
      );
      console.log('[MediaCall:11Labs] First message:', firstMessage);

      // Create a flag to track component mount state
      let isMountedFlag = true;

      // Install the global error handler before initializing the conversation
      // to catch any WebSocket errors that might occur during initialization
      const originalErrorHandler = window.onerror;
      window.onerror = (message, source, lineno, colno, error) => {
        // Only handle WebSocket errors from 11Labs
        if (
          message &&
          (message.toString().includes('WebSocket') ||
            (error && error.message && error.message.includes('WebSocket')))
        ) {
          console.warn('[MediaCall:11Labs] Caught WebSocket error:', message);
          // Don't propagate the error - we're handling it
          return true;
        }
        // Otherwise, let the original handler process it
        return originalErrorHandler
          ? originalErrorHandler(message, source, lineno, colno, error)
          : false;
      };

      // Store the handler for cleanup
      originalErrorHandlerRef.current = originalErrorHandler;

      console.log('[MediaCall:11Labs] Creating conversation session config');
      const sessionConfig = {
        agentId: ELEVEN_LABS_OCTAVE_AGENT_ID,
        connectionType: "webrtc" as const,
        overrides: {
          agent: {
            prompt: {
              prompt: instructions,
            },
            firstMessage: firstMessage,
          },
          // tts: {
          //   voiceId: moduleInfo.voiceId,
          // },
        },
        // Disable audio input to prevent worklet issues
        disableAudioInput: true,
        // Use a more robust connection handler approach
        onConnect: () => {
          console.log('[MediaCall:11Labs] Connected successfully');

          // Safety check to not update state if component unmounted
          if (!isMountedFlag) {
            console.log(
              '[MediaCall:11Labs] Component unmounted, skipping state updates',
            );
            return;
          }

          // Wait briefly before updating state to ensure connection is stable
          setTimeout(() => {
            if (isMountedFlag) {
              console.log(
                '[MediaCall:11Labs] Setting connected state after delay',
              );
              startTimeRef.current = Date.now();
              setIsConnected(true);
              setLoadingState('ready');
            }
          }, 1000); // Increased delay to ensure WebSocket is ready
        },
        onDisconnect: () => {
          console.log('[MediaCall:11Labs] Disconnected');

          // Safety check to not update state if component unmounted
          if (!isMountedFlag) return;

          setIsAISpeaking(false);
        },
        onError: (error: string) => {
          const errorMessage = error || 'Connection Error';
          console.error('[MediaCall:11Labs] Error:', errorMessage);

          // Safety check to not update state if component unmounted
          if (!isMountedFlag) return;

          setError(errorMessage);
          setLoadingState('error');
        },
        onModeChange: ({ mode }: { mode: string }) => {
          console.log(`[MediaCall:11Labs] Mode changed to: ${mode}`);

          // Safety check to not update state if component unmounted
          if (!isMountedFlag) return;

          // Using a safer approach to update speaking state
          const isNowSpeaking = mode === 'speaking';
          console.log(
            '[MediaCall:11Labs] Updating speaking state to:',
            isNowSpeaking,
          );
          setIsAISpeaking(isNowSpeaking);
        },
        onMessage: ({
          source,
          message,
        }: {
          source: 'ai' | 'user';
          message: string;
        }) => {
          // Log message with truncation for long messages
          console.log(
            `[MediaCall:11Labs] ${source === 'ai' ? 'AI' : 'User'} message:`,
            message.substring(0, 100) + (message.length > 100 ? '...' : ''),
          );

          // Store messages for session summary
          addMessageToTranscript(
            source === 'ai' ? 'coach' : 'user',
            message,
            '11Labs_onMessage'
          );
        },
      };

      // Start the session with more robust error handling
      console.log('[MediaCall:11Labs] Starting session with configuration');
      const conversation = await Conversation.startSession(sessionConfig);
      console.log('[MediaCall:11Labs] Session started successfully');

      // Store a reference to the conversation object, but also a way to restore the original handler
      const cleanup = () => {
        // Restore the original error handler if it's still our custom one
        if (window.onerror !== originalErrorHandler && originalErrorHandler) {
          console.log(
            '[MediaCall:11Labs] Restoring original error handler on cleanup',
          );
          window.onerror = originalErrorHandler;
        }

        // Mark unmounted to prevent further state updates
        isMountedFlag = false;
      };

      // Add the cleanup function to the conversation object for reference
      (conversation as any).cleanup = cleanup;

      // Set the conversation reference for the component
      conversationRef.current = conversation;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to connect to AI Coach';
      console.error('[MediaCall:11Labs] Connection error:', err);
      setError(errorMessage);
      setLoadingState('error');

      // Restore original error handler on error
      if (
        window.onerror !== originalErrorHandlerRef.current &&
        originalErrorHandlerRef.current
      ) {
        console.log(
          '[MediaCall:11Labs] Restoring original error handler after error',
        );
        window.onerror = originalErrorHandlerRef.current;
      }
    }
  };

  // Generate HeyGen session token
  // Comprehensive cleanup function for HeyGen avatar to prevent resource leaks and credit consumption
  const cleanupHeyGenAvatar = () => {
    console.log('[MediaCall:HeyGen] Running comprehensive cleanup procedure');

    try {
      if (streamingAvatarRef.current) {
        // Step 1: Stop any active processes and interrupt ongoing activities
        console.log('[MediaCall:HeyGen] Interrupting any ongoing activities');
        try {
          streamingAvatarRef.current.interrupt();
        } catch (e) {
          console.warn('Error interrupting avatar:', e);
        }

        // Step 2: Close voice chat to stop audio processing
        console.log('[MediaCall:HeyGen] Closing voice chat');
        try {
          streamingAvatarRef.current.closeVoiceChat();
        } catch (e) {
          console.warn('Error closing voice chat:', e);
        }

        // Step 3: Stop listening to ensure microphone is released
        console.log('[MediaCall:HeyGen] Stopping listening');
        try {
          streamingAvatarRef.current.stopListening();
        } catch (e) {
          console.warn('Error stopping listening:', e);
        }

        // Step 4: Remove all event listeners
        console.log('[MediaCall:HeyGen] Removing event listeners');
        if (eventListenersRef.current.length > 0) {
          eventListenersRef.current.forEach(({ type, handler }) => {
            try {
              streamingAvatarRef.current.off(type, handler);
              console.log(`[MediaCall:HeyGen] Removed listener for ${type}`);
            } catch (e) {
              console.warn(`Error removing listener for ${type}:`, e);
            }
          });
          eventListenersRef.current = [];
        }

        // Step 5: CRITICAL - Call stopAvatar to explicitly terminate the session
        console.log('[MediaCall:HeyGen] Explicitly stopping avatar');
        try {
          streamingAvatarRef.current.stopAvatar();
        } catch (e) {
          console.warn('Error stopping avatar:', e);
        }

        // Step 6: Clear video element source if it exists
        if (videoElementRef.current) {
          console.log('[MediaCall:HeyGen] Clearing video source');
          videoElementRef.current.srcObject = null;
          videoElementRef.current = null;
        }

        // Step 7: Clear video container
        if (videoRef.current) {
          console.log('[MediaCall:HeyGen] Clearing video container');
          videoRef.current.innerHTML = '';
        }

        // Step 8: Release avatar reference
        console.log('[MediaCall:HeyGen] Releasing avatar reference');
        streamingAvatarRef.current = null;
      }

      // Remove page lifecycle event listeners
      if (visibilityChangeListenerRef.current) {
        document.removeEventListener(
          'visibilitychange',
          visibilityChangeListenerRef.current,
        );
        visibilityChangeListenerRef.current = null;
        console.log('[MediaCall:HeyGen] Removed visibilitychange listener');
      }

      if (beforeUnloadListenerRef.current) {
        window.removeEventListener(
          'beforeunload',
          beforeUnloadListenerRef.current,
        );
        beforeUnloadListenerRef.current = null;
        console.log('[MediaCall:HeyGen] Removed beforeunload listener');
      }

      console.log('[MediaCall:HeyGen] Cleanup completed successfully');
    } catch (error) {
      console.error('[MediaCall:HeyGen] Error during cleanup:', error);
    }
  };

  const generateHeyGenSessionToken = async (apiKey: string) => {
    // First check if we already have a preloaded token from the mode selection screen
    if (preloadedToken) {
      console.log('[MediaCall:HeyGen] Using pre-fetched session token');
      return preloadedToken;
    }

    // Otherwise, generate a new token
    console.log(
      '[MediaCall:HeyGen] No pre-fetched token available, generating new session token',
    );
    try {
      // Generate a new token using our existing hook function
      const newToken = await generateToken(true);

      if (!newToken) {
        throw new Error('Failed to generate token');
      }

      console.log(
        '[MediaCall:HeyGen] Successfully generated new session token',
      );
      return newToken;
    } catch (error) {
      console.error(
        '[MediaCall:HeyGen] Error generating session token:',
        error,
      );
      throw error;
    }
  };

  // Setup page visibility event handlers to detect when user leaves or minimizes the page
  const setupPageLifecycleEvents = () => {
    console.log('[MediaCall:HeyGen] Setting up page lifecycle event handlers');

    // Handle visibilitychange event (when user switches tabs or minimizes browser)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        console.log('[MediaCall:HeyGen] Page hidden - session continues running in background');
        // Just log the state change - don't interrupt the session
        // This allows users to switch tabs without disrupting their coaching session
      } else if (document.visibilityState === 'visible') {
        console.log('[MediaCall:HeyGen] Page visible again - session continues normally');
        // No need to restart anything since we didn't stop it
      }
    };

    // Handle beforeunload event (when user closes tab or navigates away)
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      console.log(
        '[MediaCall:HeyGen] Page unloading, performing emergency cleanup',
      );

      // Full cleanup on page unload is critical to avoid wasting credits
      if (streamingAvatarRef.current) {
        try {
          // Critical: Must call stopAvatar() to properly terminate the session
          streamingAvatarRef.current.interrupt();
          streamingAvatarRef.current.closeVoiceChat();
          streamingAvatarRef.current.stopListening();
          streamingAvatarRef.current.stopAvatar();
          console.log('[MediaCall:HeyGen] Emergency cleanup completed');
        } catch (e) {
          console.warn('[MediaCall:HeyGen] Error in emergency cleanup:', e);
        }
      }

      // Standard beforeunload message
      e.preventDefault();
      e.returnValue = '';
    };

    // Store references for cleanup
    visibilityChangeListenerRef.current = handleVisibilityChange;
    beforeUnloadListenerRef.current = handleBeforeUnload;

    // Add the event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    console.log(
      '[MediaCall:HeyGen] Page lifecycle event handlers set up successfully',
    );
  };

  const initializeHeyGen = async (retryAttempt = 0): Promise<void> => {
    console.log(`[MediaCall:HeyGen] Initializing HeyGen video call (attempt ${retryAttempt + 1})`);

    // Update session state
    setSessionState(retryAttempt > 0 ? SessionState.RETRY : SessionState.INITIALIZING);
    setSessionStatusMessage(retryAttempt > 0 ? 'Reconnecting to your coach...' : 'Setting up your coaching session...');

    try {
      // Set up instruction and first message with user name
      const instructions = moduleInfo.instructions;
      const userName = moduleInfo.userName || DEFAULT_APP_USER_NAME;
      const firstMessage =
        moduleInfo.firstMessage ||
        `Hello ${userName}, I'm ${moduleInfo.coachName}, your coach for today's session. I'm here to guide and support you. What would you like to focus on in our conversation today?`;

      // Ensure we're using the June avatar
      const avatarId = HEYGEN_AVATARS.JUNE;
      console.log('[MediaCall:HeyGen] Using June avatar ID:', avatarId);
      console.log('[MediaCall:HeyGen] Using voice ID:', moduleInfo.voiceId);
      console.log('[MediaCall:HeyGen] First message:', firstMessage);

      // Get API key from environment
      const apiKey = import.meta.env.VITE_HEYGEN_API_TOKEN;
      if (!apiKey) {
        throw new Error('HeyGen API key not found in environment variables');
      }
      console.log('[MediaCall:HeyGen] Found API key');

      // Step 1: Generate a session token using the API key
      console.log('[MediaCall:HeyGen] Requesting a new session token');
      const sessionToken = await generateHeyGenSessionToken(apiKey);
      console.log('[MediaCall:HeyGen] Received session token');

      // Step 2: Create StreamingAvatar instance with the session token
      console.log(
        '[MediaCall:HeyGen] Creating StreamingAvatar instance with session token',
      );
      const streamingAvatar = new StreamingAvatar({
        token: sessionToken,
      });

      // Universal event logging - capture ALL events that actually fire
      const universalEventHandler = (eventType: string) => (event: any) => {
        console.log(`[MediaCall:HeyGen] 🎯 EVENT FIRED: ${eventType}`, {
          timestamp: new Date().toISOString(),
          eventType,
          eventData: event,
          sessionState,
          isConnected
        });
      };

      // Add universal logging for all known StreamingAvatar events
      const allKnownEvents = [
        'STREAM_READY',
        'STREAM_DISCONNECTED',
        'AVATAR_START_TALKING',
        'AVATAR_STOP_TALKING',
        'AVATAR_END_MESSAGE',
        'USER_START',
        'USER_STOP',
        'USER_END_MESSAGE',
        'USER_TALKING_MESSAGE',
        'AVATAR_TALKING_MESSAGE',
        'STREAM_READY_TIMEOUT'
      ];

      allKnownEvents.forEach(eventType => {
        streamingAvatar.on(eventType, universalEventHandler(eventType));
      });

      // Set up event handlers with proper tracking for cleanup
      console.log('[MediaCall:HeyGen] Setting up event handlers with tracking');

      // Clear any existing event listeners from previous sessions
      eventListenersRef.current = [];

      // Helper function to properly track event listeners for later cleanup
      const registerEventListener = (
        eventType: string,
        handler: (event: any) => void,
      ) => {
        console.log(
          `[MediaCall:HeyGen] Registering event listener for ${eventType}`,
        );
        streamingAvatar.on(eventType, handler);
        eventListenersRef.current.push({ type: eventType, handler });
      };

      // STREAM_DISCONNECTED event
      const disconnectHandler = (event: any) => {
        console.log('[MediaCall:HeyGen] 🔌 STREAM_DISCONNECTED:', {
          timestamp: new Date().toISOString(),
          sessionState: sessionState,
          isConnected: isConnected,
          wasVoiceChatReady: isVoiceChatReady.current,
          reason: event?.reason || 'Unknown reason',
          eventData: event
        });
        setIsConnected(false);

        // Ensure proper cleanup when disconnected
        setTimeout(() => {
          if (streamingAvatarRef.current) {
            console.log(
              '[MediaCall:HeyGen] Running cleanup after disconnection',
            );
            cleanupHeyGenAvatar();
          }
        }, 100);
      };
      registerEventListener(
        StreamingEvents.STREAM_DISCONNECTED,
        disconnectHandler,
      );

      // Enhanced speaking events with phase management
      const startTalkingHandler = (event: any) => {
        console.log('[MediaCall:HeyGen] 🎯 AVATAR_START_TALKING:', {
          timestamp: new Date().toISOString(),
          sessionState: sessionState,
          firstMessageSpoken: firstMessageSpoken.current,
          isUserSpeaking: isUserSpeaking,
          currentUserMessage: currentUserMessageRef.current,
          eventData: event
        });

        // If user is speaking, we might want to interrupt them (for urgent corrections)
        // But for now, let's be more polite and wait
        if (isUserSpeaking && currentUserMessageRef.current) {
          console.log('[MediaCall:HeyGen] ⚠️ Avatar trying to speak while user is talking - allowing interrupt for coaching');
          // Don't block avatar - coach should be able to guide conversation
        }

        setIsAISpeaking(true);

        // Reset avatar message and mark as speaking
        currentAvatarMessageRef.current = '';
        isAvatarSpeakingRef.current = true;
        console.log('[MediaCall:Transcript] 🤖 Avatar started speaking - reset message buffer');

        // Block user microphone during avatar speech for elegant turn-taking
        if (streamingAvatarRef.current && sessionState === SessionState.FULLY_ACTIVE) {
          console.log('[MediaCall:HeyGen] 🔇 Blocking user microphone during avatar speech');
          streamingAvatarRef.current.muteInputAudio();
          setIsMicrophoneBlocked(true);
          setTurnTakingMessage('Your coach is speaking. Please wait...');

          // Safety timeout: unblock after 30 seconds in case avatar_stop_talking event is missed
          if (turnTakingTimeoutRef.current) {
            clearTimeout(turnTakingTimeoutRef.current);
          }
          turnTakingTimeoutRef.current = setTimeout(() => {
            console.log('[MediaCall:HeyGen] ⚠️ Turn-taking timeout - force unblocking microphone');
            if (streamingAvatarRef.current && !isMuted) {
              streamingAvatarRef.current.unmuteInputAudio();
              setIsMicrophoneBlocked(false);
              setTurnTakingMessage(null);
            }
          }, 30000); // 30 second safety timeout
        }

        // Check if this is the first message
        if (!firstMessageSpoken.current) {
          console.log('[MediaCall:HeyGen] This is the first message from avatar');
          setSessionState(SessionState.AVATAR_SPEAKING_FIRST);
          setSessionStatusMessage('Your coach is greeting you...');
        }
      };
      registerEventListener(
        StreamingEvents.AVATAR_START_TALKING,
        startTalkingHandler,
      );

      const stopTalkingHandler = (event: any) => {
        console.log('[MediaCall:HeyGen] 🔇 AVATAR_STOP_TALKING:', {
          timestamp: new Date().toISOString(),
          sessionState: sessionState,
          firstMessageSpoken: firstMessageSpoken.current,
          isVoiceChatReady: isVoiceChatReady.current,
          currentAvatarMessage: currentAvatarMessageRef.current,
          eventData: event
        });
        setIsAISpeaking(false);

        // Finalize avatar message as backup if AVATAR_END_MESSAGE doesn't fire
        if (isAvatarSpeakingRef.current && currentAvatarMessageRef.current) {
          console.log('[MediaCall:Transcript] 🔇 Finalizing avatar message from AVATAR_STOP_TALKING as backup');
          finalizeMessage('coach', currentAvatarMessageRef.current, 'AVATAR_STOP_TALKING (backup)');
        }

        // Always reset avatar speaking state when avatar stops talking
        currentAvatarMessageRef.current = '';
        isAvatarSpeakingRef.current = false;

        // Unblock user microphone after avatar finishes speaking
        if (streamingAvatarRef.current && isMicrophoneBlocked && !isMuted) {
          console.log('[MediaCall:HeyGen] 🎤 Unblocking user microphone after avatar finished');

          // Clear the safety timeout since avatar properly finished
          if (turnTakingTimeoutRef.current) {
            clearTimeout(turnTakingTimeoutRef.current);
            turnTakingTimeoutRef.current = null;
          }

          // Small delay to ensure avatar has completely finished
          setTimeout(() => {
            if (streamingAvatarRef.current && !isMuted) {
              streamingAvatarRef.current.unmuteInputAudio();
              setIsMicrophoneBlocked(false);
              setTurnTakingMessage(null);
            }
          }, 200);
        }

        // If this was the first message, now enable voice chat
        if (!firstMessageSpoken.current) {
          firstMessageSpoken.current = true;
          console.log('[MediaCall:HeyGen] First message completed, preparing voice chat');
          setSessionState(SessionState.VOICE_READY);
          setSessionStatusMessage('Microphone ready - you can speak now');

          // Enable voice chat after a brief delay
          setTimeout(() => {
            if (streamingAvatarRef.current && !isVoiceChatReady.current) {
              console.log('[MediaCall:HeyGen] Starting voice chat after first message');
              startVoiceChat();
            }
          }, SESSION_INIT_CONFIG.voiceChatDelay);
        }
      };
      registerEventListener(
        StreamingEvents.AVATAR_STOP_TALKING,
        stopTalkingHandler,
      );

      // Avatar message events - capture real-time messages as recommended by HeyGen
      const avatarTalkingMessageHandler = (event: any) => {
        // Try multiple ways to extract message content
        let messageContent = '';
        if (event?.detail?.message) {
          messageContent = event.detail.message;
        } else if (event?.message) {
          messageContent = event.message;
        } else if (event?.text) {
          messageContent = event.text;
        } else if (typeof event === 'string') {
          messageContent = event;
        }

        console.log('[MediaCall:HeyGen] 💬 AVATAR_TALKING_MESSAGE:', {
          timestamp: new Date().toISOString(),
          message: messageContent,
          messageLength: messageContent?.length || 0,
          currentBufferLength: currentAvatarMessageRef.current.length,
          isAvatarSpeaking: isAvatarSpeakingRef.current,
          eventKeys: event ? Object.keys(event) : [],
          eventDetail: event?.detail,
          eventDetailKeys: event?.detail ? Object.keys(event.detail) : [],
          fullEvent: event
        });

        // Process message content regardless of speaking state (fallback for missing START events)
        if (messageContent) {
          // Ensure we're marked as speaking if we receive a TALKING_MESSAGE
          if (!isAvatarSpeakingRef.current) {
            console.log('[MediaCall:Transcript] 🤖 Avatar TALKING_MESSAGE received without START - auto-enabling speaking state');
            isAvatarSpeakingRef.current = true;
          }

          // Accumulate message chunks to build the complete message
          // Each TALKING_MESSAGE event contains a chunk, not the full message
          currentAvatarMessageRef.current += messageContent;

          console.log('[MediaCall:Transcript] 🤖 Avatar message updated:', {
            chunkLength: messageContent.length,
            totalLength: currentAvatarMessageRef.current.length,
            preview: currentAvatarMessageRef.current.substring(0, 100) + (currentAvatarMessageRef.current.length > 100 ? '...' : ''),
            isComplete: currentAvatarMessageRef.current.endsWith('.') || currentAvatarMessageRef.current.endsWith('?') || currentAvatarMessageRef.current.endsWith('!')
          });

          // Schedule finalization in case END_MESSAGE event doesn't fire
          scheduleMessageFinalization('coach', 3000);
        }
      };
      registerEventListener(StreamingEvents.AVATAR_TALKING_MESSAGE, avatarTalkingMessageHandler);

      const avatarEndMessageHandler = (event: any) => {
        const eventMessage = event?.detail?.message || event?.message || event;

        console.log('[MediaCall:HeyGen] ✅ AVATAR_END_MESSAGE:', {
          timestamp: new Date().toISOString(),
          eventMessage: eventMessage,
          currentBuiltMessage: currentAvatarMessageRef.current,
          isAvatarSpeaking: isAvatarSpeakingRef.current
        });

        // Clear any pending timeout finalization
        if (avatarMessageTimeoutRef.current) {
          clearTimeout(avatarMessageTimeoutRef.current);
          avatarMessageTimeoutRef.current = null;
        }

        // Finalize the complete avatar message when speaking ends
        if (isAvatarSpeakingRef.current && currentAvatarMessageRef.current) {
          console.log('[MediaCall:Transcript] ✅ Finalizing complete avatar message:', {
            length: currentAvatarMessageRef.current.length,
            content: currentAvatarMessageRef.current
          });

          finalizeMessage('coach', currentAvatarMessageRef.current, 'AVATAR_END_MESSAGE');

          // Reset state for next message
          currentAvatarMessageRef.current = '';
          isAvatarSpeakingRef.current = false;
        }
      };
      registerEventListener(StreamingEvents.AVATAR_END_MESSAGE, avatarEndMessageHandler);

      // Add handler for user messages - capture real-time messages as recommended by HeyGen
      const userTalkingMessageHandler = (event: any) => {
        const messageContent = event?.detail?.message || event?.message || (typeof event === 'string' ? event : '');

        console.log('[MediaCall:HeyGen] 💬 USER_TALKING_MESSAGE:', {
          timestamp: new Date().toISOString(),
          message: messageContent,
          messageLength: messageContent?.length || 0,
          currentBufferLength: currentUserMessageRef.current.length,
          isUserSpeaking: isUserSpeakingRef.current,
          eventStructure: event
        });

        // Process message content regardless of speaking state (fallback for missing START events)
        if (messageContent) {
          // Ensure we're marked as speaking if we receive a TALKING_MESSAGE
          if (!isUserSpeakingRef.current) {
            console.log('[MediaCall:Transcript] 👤 User TALKING_MESSAGE received without START - auto-enabling speaking state');
            isUserSpeakingRef.current = true;
          }

          // Accumulate message chunks to build the complete message
          // Each TALKING_MESSAGE event contains a chunk, not the full message
          currentUserMessageRef.current += messageContent;

          console.log('[MediaCall:Transcript] 👤 User message updated:', {
            chunkLength: messageContent.length,
            totalLength: currentUserMessageRef.current.length,
            preview: currentUserMessageRef.current.substring(0, 100) + (currentUserMessageRef.current.length > 100 ? '...' : ''),
            isComplete: currentUserMessageRef.current.endsWith('.') || currentUserMessageRef.current.endsWith('?') || currentUserMessageRef.current.endsWith('!')
          });

          // Schedule finalization in case END_MESSAGE event doesn't fire
          scheduleMessageFinalization('user', 2000);
        }
      };
      registerEventListener(StreamingEvents.USER_TALKING_MESSAGE, userTalkingMessageHandler);

      const userEndMessageHandler = (event: any) => {
        const eventMessage = event?.detail?.message || event?.message || event;

        console.log('[MediaCall:HeyGen] ✅ USER_END_MESSAGE:', {
          timestamp: new Date().toISOString(),
          eventMessage: eventMessage,
          currentBuiltMessage: currentUserMessageRef.current,
          isUserSpeaking: isUserSpeakingRef.current
        });

        // Clear any pending timeout finalization
        if (userMessageTimeoutRef.current) {
          clearTimeout(userMessageTimeoutRef.current);
          userMessageTimeoutRef.current = null;
        }

        // Finalize the complete user message when speaking ends
        if (isUserSpeakingRef.current && currentUserMessageRef.current) {
          console.log('[MediaCall:Transcript] ✅ Finalizing complete user message:', {
            length: currentUserMessageRef.current.length,
            content: currentUserMessageRef.current
          });

          finalizeMessage('user', currentUserMessageRef.current, 'USER_END_MESSAGE');

          // Reset state for next message
          currentUserMessageRef.current = '';
          isUserSpeakingRef.current = false;
        }
      };
      registerEventListener(StreamingEvents.USER_END_MESSAGE, userEndMessageHandler);

      // Add handlers for user interaction states
      const userStartHandler = (event: any) => {
        console.log('[MediaCall:HeyGen] 🎤 USER_START:', {
          timestamp: new Date().toISOString(),
          sessionState: sessionState,
          isAISpeaking: isAISpeaking,
          currentAvatarMessage: currentAvatarMessageRef.current,
          eventData: event
        });

        // Prevent user from speaking while avatar is speaking
        if (isAISpeaking && streamingAvatarRef.current) {
          console.log('[MediaCall:HeyGen] 🚫 Blocking user speech - avatar is currently speaking');
          try {
            streamingAvatarRef.current.muteInputAudio();
            setIsMicrophoneBlocked(true);
            setTurnTakingMessage('Please wait for your coach to finish speaking...');
            return; // Don't process user start if avatar is speaking
          } catch (err) {
            console.warn('[MediaCall:HeyGen] Failed to mute user input:', err);
          }
        }

        setIsUserSpeaking(true);

        // Reset user message and mark as speaking
        currentUserMessageRef.current = '';
        isUserSpeakingRef.current = true;
        console.log('[MediaCall:Transcript] 👤 User started speaking - reset message buffer');
      };
      registerEventListener(StreamingEvents.USER_START, userStartHandler);

      const userStopHandler = (event: any) => {
        console.log('[MediaCall:HeyGen] 🔇 USER_STOP:', {
          timestamp: new Date().toISOString(),
          sessionState: sessionState,
          currentUserMessage: currentUserMessageRef.current,
          isUserSpeaking: isUserSpeakingRef.current,
          eventData: event
        });
        setIsUserSpeaking(false);

        // Finalize user message as backup if USER_END_MESSAGE doesn't fire
        if (isUserSpeakingRef.current && currentUserMessageRef.current) {
          console.log('[MediaCall:Transcript] 🔇 Finalizing user message from USER_STOP as backup');
          finalizeMessage('user', currentUserMessageRef.current, 'USER_STOP (backup)');
        }

        // Always reset user speaking state when user stops talking
        currentUserMessageRef.current = '';
        isUserSpeakingRef.current = false;
      };
      registerEventListener(StreamingEvents.USER_STOP, userStopHandler);

      // Add handler for avatar ready
      const avatarReadyHandler = (event: any) => {
        console.log('[MediaCall:HeyGen] 📺 STREAM_READY (Avatar Ready):', {
          timestamp: new Date().toISOString(),
          sessionState: sessionState,
          eventData: event
        });
      };
      registerEventListener(StreamingEvents.STREAM_READY, avatarReadyHandler);

      // Stream ready event - crucial for displaying the avatar
      const streamReadyHandler = (event: any) => {
        console.log('[MediaCall:HeyGen] 📺 STREAM_READY (Main Handler):', {
          timestamp: new Date().toISOString(),
          sessionState: sessionState,
          hasVideoRef: !!videoRef.current,
          eventDetail: event.detail,
          eventData: event
        });

        // Update session state to avatar ready
        setSessionState(SessionState.AVATAR_READY);
        setSessionStatusMessage(`${moduleInfo.coachName} is getting ready...`);

        // Set connection states
        setIsConnected(true);
        setLoadingState('ready');

        // Wait a brief moment to ensure videoRef is available after any React rendering
        setTimeout(() => {
          // Check if videoRef and stream details are available
          if (videoRef.current && event.detail) {
            console.log(
              '[MediaCall:HeyGen] Appending video element to container',
            );

            // Get the video element from the MediaStream
            let videoElement;

            // Check if htmlElement is provided directly
            if (event.detail.htmlElement) {
              videoElement = event.detail.htmlElement;
            }
            // Otherwise create one from the MediaStream
            else if (event.detail instanceof MediaStream) {
              videoElement = document.createElement('video');
              videoElement.srcObject = event.detail;
              videoElement.autoplay = true;
              videoElement.playsInline = true;
              videoElement.style.width = '100%';
              videoElement.style.height = '100%';
              videoElement.style.objectFit = 'cover';
            }

            if (videoElement) {
              // Store reference to video element for proper cleanup
              videoElementRef.current = videoElement;

              // Clear any existing content
              videoRef.current.innerHTML = '';
              videoRef.current.appendChild(videoElement);

              // Start with welcome message after a delay to ensure the avatar is ready
              setTimeout(() => {
                if (streamingAvatar && streamingAvatarRef.current) {
                  console.log(
                    '[MediaCall:HeyGen] Speaking first message (coach perspective):',
                    firstMessage.substring(0, 50) + '...',
                  );
                  console.log(
                    '[MediaCall:HeyGen] Full first message:',
                    firstMessage,
                  );
                  streamingAvatar.speak({
                    text: firstMessage,
                    taskType: TaskType.TALK,
                    taskMode: TaskMode.SYNC,
                  });
                }
              }, 1500); // Increased delay for stability
            } else {
              console.error(
                '[MediaCall:HeyGen] Could not create video element from stream data',
              );
            }
          } else {
            console.warn('[MediaCall:HeyGen] Could not append video element:', {
              videoRefExists: !!videoRef.current,
              detailExists: !!event.detail,
              eventDetailType: event.detail ? typeof event.detail : 'undefined',
            });
          }
        }, 200); // Small delay to ensure DOM is updated
      };
      registerEventListener(StreamingEvents.STREAM_READY, streamReadyHandler);

      // Message handlers for conversation transcripts are handled by the comprehensive
      // AVATAR_TALKING_MESSAGE/AVATAR_END_MESSAGE and USER_TALKING_MESSAGE/USER_END_MESSAGE
      // event handlers implemented above

      // Step 3: Configure the avatar session according to HeyGen docs
      console.log(
        '[MediaCall:HeyGen] Creating avatar session with June_HR_public',
      );

      // Format the instructions to properly set the AI coach persona
      // Include mode-specific instructions based on interactionMode with clarity about roles
      const formattedInstructions = `
You are an AI coach named ${moduleInfo.coachName} providing guidance on ${
        moduleInfo.title
      }.
The person you are coaching is named ${
        moduleInfo.userName || DEFAULT_APP_USER_NAME
      }.
${instructions}

QUANTUM LEADERSHIP & ONTOLOGICAL COACHING PRINCIPLES:
1. Focus on the client's way of being, not just their actions
2. Observe language patterns as pathways to deeper understanding
3. Honor the client's unique perspective and lived experience
4. Create a safe space for authentic exploration
5. Facilitate shifts in perception that enable new possibilities

CRITICAL ROLE CLARITY:
1. YOU ARE THE COACH - The person interacting with you is the CLIENT
2. Do NOT introduce yourself or speak as if you are the one seeking coaching
3. Always maintain your role as the coach providing guidance and support
4. The user is coming to you for coaching - they are never the coach
5. Assume the user has zero coaching training - you are the expert

IMPORTANT GUIDELINES:
1. You are the coach, and the user is seeking your guidance and expertise
2. Speak directly to the user as if you are having a real coaching conversation
3. Ask thoughtful questions to understand the user's situation
4. Provide practical advice and actionable suggestions
5. Be empathetic, supportive, and positive
6. Keep your responses concise and focused
7. Never confuse roles - you are always the coach, never the client


VIDEO CALL WITH YOUR PRECIOUS CLIENT:
You are on a VIDEO CALL with your valued client. Treat this as a personal, intimate coaching session.

HOW TO SPEAK:
- Keep responses SHORT: Maximum 2-3 sentences per turn
- Speak naturally as if you're sitting across from them
- Use conversational language: "I can see you're..." or "I notice..."
- Reference what you observe: "Your smile tells me..." or "I see some tension..."
- NO bullet points, lists, or formal structure in your speech
- Pause naturally to allow dialogue - this is NOT a monologue
- Start warmly: "Hi ${moduleInfo.userName || DEFAULT_APP_USER_NAME}, good to see you. What's on your mind today?"

`;

      console.log(
        '[MediaCall:HeyGen] Formatted coaching instructions:',
        formattedInstructions.substring(0, 200) + '...',
      );

      const sessionConfig = {
        // Avatar settings - June_HR_public is a pre-built avatar
        avatarName: avatarId,
        quality: AvatarQuality.Low, // Keep Low quality as requested

        // AI configuration with enhanced coaching instructions
        knowledgeBase: formattedInstructions,

        // Voice settings optimized for coaching
        voice: {
          rate: 0.95, // Slightly slower for better clarity
        },

        // Language setting
        language: 'en-US',

        disableIdleTimeout: true, // Disable idle timeout to prevent session drop
        // Disable auto-reconnect to avoid unexpected behavior

        // Enable audio processing for better voice interaction
        // enableAudioProcessing: true,

        // Explicitly enable STT (Speech-to-Text) for proper voice recognition
        // sttEnabled: true,

        // Streaming configuration
        // preferredStreamingFormat: 'webrtc',

        // Improve performance with optimized settings
        ICEServers: undefined,
        useAvatarBackground: false,
      };

      console.log(
        '[MediaCall:HeyGen] Avatar session config:',
        JSON.stringify(sessionConfig, null, 2),
      );

      // Step 4: Create and start the avatar session
      console.log('[MediaCall:HeyGen] Starting avatar session...');

      try {
        // Create and start the avatar with improved error handling
        const sessionData = await streamingAvatar.createStartAvatar(
          sessionConfig,
        );
        console.log(
          '[MediaCall:HeyGen] Avatar session created successfully:',
          sessionData,
        );

        // Set up page lifecycle event handlers to prevent credit waste
        console.log('[MediaCall:HeyGen] Setting up page lifecycle listeners');
        setupPageLifecycleEvents();

        // We successfully started a session, so store our reference
        streamingAvatarRef.current = streamingAvatar;

        // Additional debug log to understand video rendering issues
        console.log(
          '[MediaCall:HeyGen] Video ref element exists:',
          !!videoRef.current,
        );

        // We don't need to handle the MediaStream here - it will be provided via the STREAM_READY event
        console.log(
          '[MediaCall:HeyGen] Session created successfully, waiting for STREAM_READY event for video rendering',
        );

        // The videoRef will be populated when the STREAM_READY event fires
        // The event handler for STREAM_READY will create and attach the video element

        // DO NOT start voice chat automatically - wait for first message to complete
        console.log('[MediaCall:HeyGen] Waiting for first message before enabling voice chat');
      } catch (sessionError) {
        console.error(
          '[MediaCall:HeyGen] Failed to create avatar session:',
          sessionError,
        );

        // Ensure we cleanup any partial session establishment
        if (streamingAvatar) {
          try {
            console.log('[MediaCall:HeyGen] Cleaning up failed session');
            streamingAvatar.stopAvatar();
          } catch (e) {
            console.warn(
              '[MediaCall:HeyGen] Error cleaning up failed session:',
              e,
            );
          }
        }

        throw sessionError; // Re-throw for the outer catch handler
      }
    } catch (err) {
      // Enhanced error handling with specific error messages
      console.error('[MediaCall:HeyGen] Connection error:', err);

      // Provide specific error messages based on error type
      if (err instanceof Error) {
        if (err.message.includes('401')) {
          setError('Authentication failed (401). Please check your API key.');
        } else if (err.message.includes('404')) {
          setError(
            'Avatar not found (404). June_HR_public avatar may not exist or is not accessible.',
          );
        } else if (err.message.includes('Network')) {
          setError('Network error. Please check your internet connection.');
        } else if (err.message.includes('token')) {
          setError(
            'Failed to generate session token. Check your API key permissions.',
          );
        } else {
          setError(`Error: ${err.message}`);
        }
      } else {
        setError('Failed to connect to HeyGen streaming service');
      }

      setLoadingState('error');
      setSessionState(SessionState.ERROR);

      // Set appropriate error message
      let errorMessage = 'Failed to connect to coaching session';
      if (err instanceof Error) {
        errorMessage = err.message;
      }
      setSessionStatusMessage(errorMessage);

      // Auto-retry logic
      if (retryAttempt < SESSION_INIT_CONFIG.maxRetries) {
        console.log(`[MediaCall:HeyGen] Retrying in ${SESSION_INIT_CONFIG.retryDelay}ms...`);
        setSessionRetryCount(retryAttempt + 1);

        setTimeout(() => {
          initializeHeyGen(retryAttempt + 1);
        }, SESSION_INIT_CONFIG.retryDelay);
      } else {
        console.error('[MediaCall:HeyGen] Max retries reached, giving up');
        setSessionStatusMessage('Connection failed - click to retry');
      }
    }
  };

  const startVoiceChat = async () => {
    console.log('[MediaCall] Starting voice chat');
    if (mode === 'video' && streamingAvatarRef.current) {
      try {
        console.log('[MediaCall:HeyGen] Starting voice chat integration');

        // Request microphone access first
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            },
          });
          console.log(
            '[MediaCall:HeyGen] Microphone access granted:',
            stream.getAudioTracks()[0].label,
          );
          console.log(
            '[MediaCall:HeyGen] Microphone settings:',
            JSON.stringify(stream.getAudioTracks()[0].getSettings(), null, 2),
          );
        } catch (micError) {
          console.error(
            '[MediaCall:HeyGen] Microphone access denied:',
            micError,
          );
          setError('Please grant microphone access to enable voice chat.');
          return;
        }

        // Use startVoiceChat instead of startListening for better integration
        console.log(
          '[MediaCall:HeyGen] Initializing voice chat with recommended settings',
        );
        await streamingAvatarRef.current.startVoiceChat({
          // Voice chat settings
          isInputAudioMuted: false,

          // Use our optimized configuration for natural conversation flow
          silenceDurationMs: VOICE_CHAT_CONFIG.silenceDurationMs,
          vadSensitivity: VOICE_CHAT_CONFIG.vadSensitivity,
          endOfSpeechDelayMs: VOICE_CHAT_CONFIG.endOfSpeechDelayMs,

          // Allow interruptions but with controlled timing
          allowInterruption: VOICE_CHAT_CONFIG.allowInterruption,
          interruptionDelayMs: VOICE_CHAT_CONFIG.interruptionDelayMs,

          // Debug mode to show more logs
          debug: true,
        });

        console.log('[MediaCall:HeyGen] Voice chat started successfully');

        // Mark voice chat as ready and update session state
        isVoiceChatReady.current = true;
        setSessionState(SessionState.FULLY_ACTIVE);
        setSessionStatusMessage('Session active');

        // Add event listeners for voice recognition debugging and UI updates
        const userStartMessageHandler = (event: any) => {
          console.log('[MediaCall:HeyGen] 🎤 USER_START (alt):', {
            timestamp: new Date().toISOString(),
            sessionState: sessionState,
            isVoiceChatReady: isVoiceChatReady.current,
            isAISpeaking: isAISpeaking,
            currentAvatarMessage: currentAvatarMessageRef.current,
            eventData: event
          });

          // Prevent user from speaking while avatar is speaking
          if (isAISpeaking && streamingAvatarRef.current) {
            console.log('[MediaCall:HeyGen] 🚫 Blocking user speech (alt) - avatar is currently speaking');
            try {
              streamingAvatarRef.current.muteInputAudio();
              setIsMicrophoneBlocked(true);
              setTurnTakingMessage('Please wait for your coach to finish speaking...');
              return; // Don't process user start if avatar is speaking
            } catch (err) {
              console.warn('[MediaCall:HeyGen] Failed to mute user input (alt):', err);
            }
          }

          setIsUserSpeaking(true);

          // Reset user message and mark as speaking
          currentUserMessageRef.current = '';
          isUserSpeakingRef.current = true;
          console.log('[MediaCall:Transcript] 👤 User started speaking (alt handler) - reset message buffer');

          // Check if we're ready for user input
          if (!isVoiceChatReady.current || sessionState !== SessionState.FULLY_ACTIVE) {
            console.warn('[MediaCall:HeyGen] User speaking too early, session not ready');
            // We can't prevent them from speaking, but we can show a visual hint
            if (sessionState === SessionState.AVATAR_SPEAKING_FIRST) {
              setSessionStatusMessage('Please wait for your coach to finish greeting you...');
            }
          }
        };
        streamingAvatarRef.current.on('USER_START', userStartMessageHandler);
        eventListenersRef.current.push({
          type: 'USER_START',
          handler: userStartMessageHandler,
        });

        const userTalkingMessageHandler = (event: any) => {
          const messageContent = event?.detail?.message || event?.text || event?.message || '';

          console.log('[MediaCall:HeyGen] 🗣️ USER_TALKING_MESSAGE (alt):', {
            timestamp: new Date().toISOString(),
            message: messageContent || 'No message text',
            messageLength: messageContent.length,
            sessionState: sessionState,
            isVoiceChatReady: isVoiceChatReady.current,
            isUserSpeaking: isUserSpeakingRef.current,
            eventData: event
          });

          // Process message content regardless of speaking state (fallback for missing START events)
          if (messageContent) {
            // Ensure we're marked as speaking if we receive a TALKING_MESSAGE
            if (!isUserSpeakingRef.current) {
              console.log('[MediaCall:Transcript] 👤 User TALKING_MESSAGE (alt) received without START - auto-enabling speaking state');
              isUserSpeakingRef.current = true;
            }

            // Update current message with the latest complete content
            currentUserMessageRef.current = messageContent;

            console.log('[MediaCall:Transcript] 👤 User message updated (alt handler):', {
              currentLength: messageContent.length,
              preview: messageContent.substring(0, 100) + (messageContent.length > 100 ? '...' : '')
            });

            // Schedule finalization in case END_MESSAGE event doesn't fire
            scheduleMessageFinalization('user', 2000);
          }
        };
        streamingAvatarRef.current.on('USER_TALKING_MESSAGE', userTalkingMessageHandler);
        eventListenersRef.current.push({
          type: 'USER_TALKING_MESSAGE',
          handler: userTalkingMessageHandler,
        });

        const userEndMessageHandler = (event: any) => {
          const eventMessage = event?.detail?.message || event?.text || event?.message || '';
          const messageContent = currentUserMessageRef.current || eventMessage;

          console.log('[MediaCall:HeyGen] ✅ USER_END_MESSAGE (alt):', {
            timestamp: new Date().toISOString(),
            eventMessage: eventMessage,
            currentBuiltMessage: currentUserMessageRef.current,
            finalMessage: messageContent,
            messageLength: messageContent.length,
            sessionState: sessionState,
            isVoiceChatReady: isVoiceChatReady.current,
            isUserSpeaking: isUserSpeakingRef.current,
            eventData: event
          });

          // Clear any pending timeout finalization
          if (userMessageTimeoutRef.current) {
            clearTimeout(userMessageTimeoutRef.current);
            userMessageTimeoutRef.current = null;
          }

          // Finalize the complete user message when speaking ends
          if (isUserSpeakingRef.current && messageContent) {
            setLastUserMessage(messageContent);

            console.log('[MediaCall:Transcript] ✅ Finalizing complete user message (alt handler):', {
              length: messageContent.length,
              content: messageContent
            });

            finalizeMessage('user', messageContent, 'USER_END_MESSAGE (alt handler)');

            // Reset state for next message
            currentUserMessageRef.current = '';
            isUserSpeakingRef.current = false;
          }
        };
        streamingAvatarRef.current.on(
          'USER_END_MESSAGE',
          userEndMessageHandler,
        );
        eventListenersRef.current.push({
          type: 'USER_END_MESSAGE',
          handler: userEndMessageHandler,
        });

        const userStopHandler = (event: any) => {
          console.log('[MediaCall:HeyGen] 🔇 USER_STOP:', {
            timestamp: new Date().toISOString(),
            sessionState: sessionState,
            isVoiceChatReady: isVoiceChatReady.current,
            isAISpeaking: isAISpeaking,
            eventData: event
          });
          setIsUserSpeaking(false);
        };
        streamingAvatarRef.current.on('USER_STOP', userStopHandler);
        eventListenersRef.current.push({
          type: 'USER_STOP',
          handler: userStopHandler,
        });

        const userSilenceHandler = (event: any) => {
          console.log('[MediaCall:HeyGen] 🤫 USER_SILENCE:', {
            timestamp: new Date().toISOString(),
            sessionState: sessionState,
            isVoiceChatReady: isVoiceChatReady.current,
            isAISpeaking: isAISpeaking,
            isUserSpeaking: isUserSpeaking,
            duration: event?.duration || 'Unknown duration',
            eventData: event
          });
        };
        streamingAvatarRef.current.on('USER_SILENCE', userSilenceHandler);
        eventListenersRef.current.push({
          type: 'USER_SILENCE',
          handler: userSilenceHandler,
        });

        // Auto-unmute if currently muted to ensure audio processing works
        if (isMuted) {
          console.log(
            '[MediaCall:HeyGen] Auto-unmuting to enable voice interaction',
          );
          setIsMuted(false);
          streamingAvatarRef.current.unmuteInputAudio();
        }
      } catch (err) {
        console.error('[MediaCall:HeyGen] Error starting voice chat:', err);
        // Detailed error information
        if (err instanceof Error) {
          console.error('[MediaCall:HeyGen] Error details:', {
            name: err.name,
            message: err.message,
            stack: err.stack,
          });
        }
        setError(
          'Failed to start voice recognition. Please check your microphone settings and refresh.',
        );
      }
    } else {
      console.log(
        '[MediaCall] Cannot start voice chat - either not in video mode or avatar not initialized',
      );
    }
  };

  const toggleMute = () => {
    const newMutedState = !isMuted;
    console.log(
      `[MediaCall] Toggling mute to: ${newMutedState ? 'muted' : 'unmuted'}`,
    );
    setIsMuted(newMutedState);

    // Apply mute action to active connection
    if (mode === 'video' && streamingAvatarRef.current) {
      try {
        if (newMutedState) {
          console.log(
            '[MediaCall:HeyGen] Muting microphone using inputAudio methods',
          );
          // Use the correct API methods for voice chat
          streamingAvatarRef.current.muteInputAudio();
        } else {
          // Check if we should allow unmuting (respect turn-taking)
          if (isAISpeaking && sessionState === SessionState.FULLY_ACTIVE) {
            console.log('[MediaCall:HeyGen] User trying to unmute while avatar is speaking - showing warning');
            setTurnTakingMessage('Please wait for your coach to finish speaking before unmuting');
            setTimeout(() => setTurnTakingMessage(null), 3000);
            // Revert the mute state since we're not actually unmuting
            setIsMuted(true);
            return;
          }

          console.log(
            '[MediaCall:HeyGen] Unmuting microphone using inputAudio methods',
          );
          streamingAvatarRef.current.unmuteInputAudio();

          // Clear any turn-taking blocks since user explicitly unmuted
          setIsMicrophoneBlocked(false);
          setTurnTakingMessage(null);

          // Check if voice chat is active, if not restart it
          setTimeout(() => {
            console.log(
              '[MediaCall:HeyGen] Checking voice chat status after unmute',
            );
            try {
              // If we detect no voice activity for a while, try restarting voice chat
              streamingAvatarRef.current
                .isVoiceChatActive()
                .then((isActive: boolean) => {
                  console.log(
                    '[MediaCall:HeyGen] Voice chat active status:',
                    isActive,
                  );
                  if (!isActive) {
                    console.log(
                      '[MediaCall:HeyGen] Voice chat not active, restarting',
                    );
                    startVoiceChat();
                  }
                });
            } catch (e) {
              console.warn(
                '[MediaCall:HeyGen] Error checking voice chat status:',
                e,
              );
            }
          }, 500);
        }
      } catch (err) {
        console.error('[MediaCall:HeyGen] Error toggling mute state:', err);
        // Attempt to recover by restarting voice chat
        if (!newMutedState) {
          console.log(
            '[MediaCall:HeyGen] Attempting to recover by restarting voice chat',
          );
          setTimeout(() => startVoiceChat(), 1000);
        }
      }
    }
  };

  const toggleVideo = async () => {
    const newVideoState = !isVideoOn;
    setIsVideoOn(newVideoState);

    if (userStream) {
      userStream.getVideoTracks().forEach(track => {
        track.enabled = newVideoState;
        if (!newVideoState) {
          // When turning off video, fully stop the track to release camera
          console.log('[MediaCall] Stopping video track to release camera');
          track.stop();
        }
      });

      // If we turned off video, clean up the stream completely
      if (!newVideoState) {
        setUserStream(null);
        if (userVideoRef.current) {
          userVideoRef.current.srcObject = null;
        }
        streamAssignedRef.current = false;
      }
    }

    // If turning on and no stream exists, request camera access
    if (newVideoState && !userStream && mode === 'video') {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: selectedVideoInput ? { exact: selectedVideoInput } : undefined,
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
          audio: false // Audio is handled by 11Labs/HeyGen
        });
        if (DEBUG_USER_VIDEO) {
          console.log('[MediaCall] User camera stream obtained in toggleVideo:', {
            videoTracks: stream.getVideoTracks().length,
            audioTracks: stream.getAudioTracks().length
          });
        }
        // Reset stream assignment flag for new stream
        streamAssignedRef.current = false;
        setUserStream(stream);
      } catch (err) {
        console.error('Error accessing camera:', err);
        setIsVideoOn(false);
      }
    }
  };

  // Handle back navigation with proper cleanup
  const handleBackNavigation = async () => {
    console.log('[MediaCall] Back button clicked - performing cleanup before navigation');

    try {
      // Mark session as ending to prevent duplicate cleanup
      setSessionEnded(true);

      // Always run comprehensive stream cleanup first
      cleanupAllStreams();

      // Clean up connections based on mode
      if (mode === 'video' && streamingAvatarRef.current) {
        console.log('[MediaCall:HeyGen] Cleaning up HeyGen before navigation');
        cleanupHeyGenAvatar();
      } else if (conversationRef.current) {
        console.log('[MediaCall:11Labs] Cleaning up 11Labs before navigation');

        // Run the custom cleanup function if available
        if (
          (conversationRef.current as any).cleanup &&
          typeof (conversationRef.current as any).cleanup === 'function'
        ) {
          (conversationRef.current as any).cleanup();
        }

        // End the session safely
        conversationRef.current.endSession();
        conversationRef.current = null;
      }

      // Set connection state
      setIsConnected(false);

      // Try to end the session gracefully (but don't wait for it)
      if (deviceId) {
        const fallbackSessionId = sessionId || `${mode}-session-${Date.now()}`;
        endSession({
          sessionId: fallbackSessionId,
          moduleId,
          sessionType: mode,
          transcriptData: {
            messages: messagesRef.current,
          },
          deviceId,
        }).catch(err => {
          console.warn('[MediaCall] Error ending session during back navigation:', err);
        });
      }

    } catch (error) {
      console.error('[MediaCall] Error during back navigation cleanup:', error);
    } finally {
      // Small delay to ensure cleanup completes before navigation
      setTimeout(() => {
        navigate('/app');
      }, 100);
    }
  };

  // Handle session end and generate summary
  const handleEndCall = async () => {
    console.log('[MediaCall] Ending call with comprehensive cleanup');

    try {
      // Finalize any remaining messages before ending session
      console.log('[MediaCall] Finalizing any remaining messages before session end');

      // Clear any pending timeout finalizations first
      if (avatarMessageTimeoutRef.current) {
        clearTimeout(avatarMessageTimeoutRef.current);
        avatarMessageTimeoutRef.current = null;
      }
      if (userMessageTimeoutRef.current) {
        clearTimeout(userMessageTimeoutRef.current);
        userMessageTimeoutRef.current = null;
      }

      // Finalize any remaining user message
      if (isUserSpeakingRef.current && currentUserMessageRef.current?.trim()) {
        console.log('[MediaCall] Finalizing remaining user message on session end:', {
          length: currentUserMessageRef.current.length,
          content: currentUserMessageRef.current.substring(0, 100) + (currentUserMessageRef.current.length > 100 ? '...' : '')
        });
        finalizeMessage('user', currentUserMessageRef.current, 'SESSION_END_USER');
        currentUserMessageRef.current = '';
        isUserSpeakingRef.current = false;
      }

      // Finalize any remaining avatar message
      if (isAvatarSpeakingRef.current && currentAvatarMessageRef.current?.trim()) {
        console.log('[MediaCall] Finalizing remaining avatar message on session end:', {
          length: currentAvatarMessageRef.current.length,
          content: currentAvatarMessageRef.current.substring(0, 100) + (currentAvatarMessageRef.current.length > 100 ? '...' : '')
        });
        finalizeMessage('coach', currentAvatarMessageRef.current, 'SESSION_END_AVATAR');
        currentAvatarMessageRef.current = '';
        isAvatarSpeakingRef.current = false;
      }

      // Use sessionId from backend, or create fallback if not available
      const fallbackSessionId = sessionId || `${mode}-session-${Date.now()}`;

      if (!sessionId) {
        console.warn(
          '[MediaCall] No sessionId from backend, using fallback:',
          fallbackSessionId
        );
      }

      if (!deviceId) {
        console.warn(
          '[MediaCall] No device ID available. Session data may not be properly attributed.',
        );
      }

      // 🔍 DEBUG: Comprehensive session end debug logging
      console.log('[MediaCall:Transcript:DEBUG] 🔚 Session ending - collecting all messages:');
      console.log('[MediaCall:Transcript:DEBUG] 📊 Message collection summary:', {
        totalMessageCount: messagesRef.current.length,
        currentAvatarMessage: currentAvatarMessageRef.current,
        currentUserMessage: currentUserMessageRef.current,
        isAvatarSpeaking: isAvatarSpeakingRef.current,
        isUserSpeaking: isUserSpeakingRef.current,
        timestamp: new Date().toISOString()
      });

      // Log each message individually for detailed debugging
      messagesRef.current.forEach((message, index) => {
        console.log(`[MediaCall:Transcript:DEBUG] 📋 Message ${index + 1}/${messagesRef.current.length}:`, {
          index,
          role: message.role,
          content: message.content,
          contentLength: message.content.length,
          timestamp: message.timestamp
        });
      });

      // Log collected messages for debugging (after finalization)
      console.log('[MediaCall] Collected messages for session end (after finalization):', {
        messageCount: messagesRef.current.length,
        messages: messagesRef.current.map(msg => ({
          role: msg.role,
          content: msg.content.substring(0, 100) + (msg.content.length > 100 ? '...' : ''),
          timestamp: msg.timestamp
        }))
      });

      // Request session summary with device ID
      const success = await endSession({
        sessionId: fallbackSessionId,
        moduleId,
        sessionType: mode,
        transcriptData: {
          messages: messagesRef.current,
        },
        deviceId,
      });

      // Mark session as ended for UI
      setSessionEnded(true);

      // Show summary dialog first, BEFORE cleaning up connections
      if (success) {
        console.log(
          '[MediaCall] Successfully retrieved summary, showing dialog',
        );
        setShowSummary(true);
      }

      // Always run comprehensive stream cleanup
      cleanupAllStreams();

      // Now clean up connections based on mode
      if (mode === 'video' && streamingAvatarRef.current) {
        try {
          console.log(
            '[MediaCall:HeyGen] Running full HeyGen cleanup procedure',
          );
          // Use our comprehensive cleanup function instead of just disconnect
          cleanupHeyGenAvatar();
        } catch (err) {
          console.warn(
            '[MediaCall:HeyGen] Error during comprehensive cleanup:',
            err,
          );
        }
      } else if (conversationRef.current) {
        try {
          console.log('[MediaCall:11Labs] Ending conversation session');

          // Run the custom cleanup function if available
          if (
            (conversationRef.current as any).cleanup &&
            typeof (conversationRef.current as any).cleanup === 'function'
          ) {
            console.log('[MediaCall:11Labs] Running custom cleanup function');
            (conversationRef.current as any).cleanup();
          }

          // End the session safely
          conversationRef.current.endSession();
          conversationRef.current = null;
        } catch (err) {
          console.warn(
            '[MediaCall:11Labs] Error ending conversation session:',
            err,
          );
        }
      }

      // Set connection state after cleanup
      setIsConnected(false);

      // If summary fails to show, redirect to home after a delay
      if (!success) {
        console.log(
          '[MediaCall] Failed to get summary, navigating home after delay',
        );
        setTimeout(() => {
          console.log('[MediaCall] Navigating back to app home');
          navigate('/app');
        }, 1000);
      }
    } catch (error) {
      console.error('[MediaCall] Error ending session:', error);
      setError('Failed to end session and get summary');

      // Navigate home after a delay if there was an error
      setTimeout(() => {
        console.log('[MediaCall] Navigating back to app home after error');
        navigate('/app');
      }, 2000);
    }
  };

  // Handle close summary and navigate home
  const handleCloseSummary = () => {
    setShowSummary(false);
    clearSummary(); // Clean up summary data
    console.log('[MediaCall] Navigating back to app home after summary view');
    navigate('/app');
  };

  // Debugging helper to log summary data status
  useEffect(() => {
    if (showSummary) {
      console.log('[MediaCall] Summary dialog state:', {
        showSummary,
        hasSummaryData: !!summaryData,
        isSummaryLoading,
        summaryError,
      });
    }
  }, [showSummary, summaryData, isSummaryLoading, summaryError]);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // Responsive layout handling
  const [showSettings, setShowSettings] = useState(false);
  const [showConnectionInfo, setShowConnectionInfo] = useState(false);
  const [audioVolume, setAudioVolume] = useState(100);

  // Show preview screen first
  if (showPreview) {
    return (
      <MediaPreview
        onStart={handlePreviewComplete}
        mode={mode}
        moduleTitle={moduleInfo.title}
        coachName={moduleInfo.coachName}
      />
    );
  }

  return (
    <div
      ref={containerRef}
      className={`flex flex-col h-screen ${
        mode === 'video'
          ? 'bg-gray-900'
          : 'bg-gray-50'
      } overflow-hidden`}
    >
      {/* Enhanced Header with responsive design */}
      {mode === 'video' ? (
        <motion.header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute top-0 left-0 right-0 z-10 px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between bg-black/50"
        >
          <div className="flex items-center overflow-hidden">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleBackNavigation}
              className="mr-3 sm:mr-4 p-2 rounded-lg hover:bg-white/10 flex-shrink-0 transition-colors"
              aria-label="Back to home"
            >
              <ArrowLeft className="h-5 w-5 text-white" />
            </motion.button>
            <div className="overflow-hidden">
              <h1 className="text-lg sm:text-xl font-semibold text-white truncate max-w-[180px] sm:max-w-sm">
                {moduleInfo.title}
              </h1>
              <div className="flex items-center mt-1">
                <motion.span
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className={`inline-block w-2 h-2 rounded-full ${
                    isConnected
                      ? 'bg-primary shadow-primary/50'
                      : 'bg-yellow-400'
                  } mr-2 shadow-sm`}
                />
                <span className="text-sm text-white/90 font-medium">
                  {formatDuration(callDuration)}
                </span>
              </div>
            </div>
          </div>

          {/* Enhanced End session button */}
          <SessionEndButton
            onClick={handleEndCall}
            variant="compact"
            className="text-xs sm:text-sm bg-red-500/20 hover:bg-red-500/30 border border-red-400/30 text-white rounded-lg px-4 py-2 transition-colors"
          />

          {/* Session status indicator for video mode */}
          {sessionState !== SessionState.FULLY_ACTIVE && (
            <div className="absolute left-1/2 transform -translate-x-1/2 top-full mt-2">
              <SessionStatusIndicator
                state={sessionState}
                message={sessionStatusMessage}
                showMicrophoneHint={true}
                onRetry={handleManualRetry}
              />
            </div>
          )}
        </motion.header>
      ) : (
        <motion.header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="sticky top-0 z-20 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between"
        >
          <div className="flex items-center overflow-hidden">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleBackNavigation}
              className="mr-3 sm:mr-4 p-2 rounded-lg hover:bg-gray-100 flex-shrink-0 transition-colors"
              aria-label="Back to home"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </motion.button>
            <div className="overflow-hidden">
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate max-w-[180px] sm:max-w-sm">
                {moduleInfo.title}
              </h1>
              <div className="flex items-center mt-1">
                <motion.span
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className={`inline-block w-2 h-2 rounded-full ${
                    isConnected
                      ? 'bg-primary shadow-primary/20'
                      : 'bg-yellow-500'
                  } mr-2 shadow-sm`}
                />
                <span className="text-sm text-gray-600 font-medium">
                  {formatDuration(callDuration)} • AI Coach Active
                </span>
              </div>
            </div>
          </div>

          {/* Enhanced End session button */}
          <SessionEndButton
            onClick={handleEndCall}
            variant="ghost"
            className="text-xs sm:text-sm whitespace-nowrap text-red-600 hover:bg-red-50 border border-red-200 rounded-lg px-4 py-2 transition-colors"
          />
        </motion.header>
      )}

      {/* Session summary overlay with improved responsiveness */}
      <AnimatePresence>
        {showSummary && summaryData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-2 sm:p-6 overflow-y-auto"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{
                type: 'spring',
                duration: 0.5,
                delay: 0.1, // Slight delay to ensure parent animation completes first
              }}
              className="relative w-full max-w-sm sm:max-w-2xl lg:max-w-4xl my-4"
            >
              <SessionSummary data={summaryData} onClose={handleCloseSummary} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Call area with improved responsiveness */}
      <div className="flex-1 relative">
        {(!isConnected || loadingState !== 'ready') && !sessionEnded ? (
          // Loading/Connecting state with improved visuals
          <div
            className={`absolute inset-0 flex items-center justify-center ${
              mode === 'video' ? 'bg-gray-900' : 'bg-gray-50'
            }`}
          >
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center max-w-xs sm:max-w-sm mx-auto px-4"
            >
              <motion.div
                animate={{
                  scale: [1, 1.05, 1],
                  opacity: [0.8, 1, 0.8],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatType: 'reverse',
                }}
                className={`mb-6 mx-auto w-16 h-16 sm:w-20 sm:h-20 rounded-full ${
                  mode === 'video' ? 'bg-gray-800' : 'bg-gray-100'
                } flex items-center justify-center`}
              >
                <Phone
                  className={`h-8 w-8 sm:h-10 sm:w-10 ${
                    mode === 'video' ? 'text-gray-300' : 'text-primary'
                  }`}
                />
              </motion.div>
              <h2
                className={`text-lg sm:text-xl font-medium ${
                  mode === 'video' ? 'text-white' : 'text-gray-800'
                } mb-2`}
              >
                {loadingState === 'connecting'
                  ? 'Connecting to Coach...'
                  : loadingState === 'error'
                  ? 'Connection Error'
                  : 'Preparing Session...'}
              </h2>
              <p
                className={`text-sm sm:text-base ${
                  mode === 'video' ? 'text-gray-300' : 'text-gray-600'
                }`}
              >
                {loadingState === 'connecting'
                  ? 'Please wait while we establish a secure connection'
                  : loadingState === 'error'
                  ? error || 'Failed to connect. Please try again.'
                  : 'Setting up your coaching session...'}
              </p>

              {loadingState === 'error' && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/app')}
                  className={`mt-6 ${
                    mode === 'video'
                      ? 'bg-gray-800 text-white hover:bg-gray-700'
                      : 'bg-white'
                  }`}
                >
                  Return to Home
                </Button>
              )}
            </motion.div>
          </div>
        ) : mode === 'video' && !sessionEnded ? (
          <>
            {/* Video call view with improved UI */}
            <div className="absolute inset-0 bg-gray-900">
              {/* Video container with responsive sizing and shadow */}
              {DISABLE_HEYGEN ? (
                // Mock avatar for development
                <div className="w-full h-full flex items-center justify-center bg-gray-600">
                  <div className="text-center text-white p-8">
                    <div className="w-32 h-32 sm:w-48 sm:h-48 rounded-full bg-white/20 mx-auto mb-4 flex items-center justify-center border-4 border-white/30">
                      <span className="text-4xl sm:text-6xl font-bold">
                        {moduleInfo.coachName.charAt(0)}
                      </span>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-semibold mb-2">
                      {moduleInfo.coachName}
                    </h3>
                    <p className="text-white/90 text-sm sm:text-base mb-2">
                      Mock Avatar (HeyGen Disabled)
                    </p>
                    <p className="text-white/70 text-xs">
                      User video testing mode
                    </p>
                    {isAISpeaking && (
                      <div className="mt-4 flex items-center justify-center">
                        <Volume2 className="h-5 w-5 mr-2 animate-pulse" />
                        <span className="text-sm">Speaking...</span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div
                  ref={videoRef}
                  className={cn(
                    'w-full h-full transition-opacity duration-300',
                    isVideoOn ? 'opacity-100' : 'opacity-0',
                  )}
                ></div>
              )}

              {/* Improved no-video fallback */}
              {!isVideoOn && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="w-full h-full flex items-center justify-center p-4"
                >
                  <div className="text-center bg-gray-800/80 p-6 sm:p-8 rounded-2xl backdrop-blur-sm max-w-sm">
                    <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gray-700 mx-auto mb-4 flex items-center justify-center border-2 border-gray-600">
                      <span className="text-3xl sm:text-4xl text-white">
                        {moduleInfo.coachName.charAt(0)}
                      </span>
                    </div>
                    <h3 className="text-xl font-medium text-white mb-2">
                      {moduleInfo.coachName}
                    </h3>
                    <p className="text-gray-300 text-sm mb-4">
                      Video is turned off
                    </p>
                    <Button
                      variant="outline"
                      onClick={toggleVideo}
                      className="text-white border-gray-600 hover:bg-gray-700"
                    >
                      <Video className="h-4 w-4 mr-2" />
                      Turn Video On
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Enhanced speaking indicator */}
              <AnimatePresence>
                {isAISpeaking && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute bottom-24 sm:bottom-28 left-1/2 transform -translate-x-1/2 bg-black/60 rounded-full px-3 sm:px-4 py-1 sm:py-2 text-white text-xs sm:text-sm backdrop-blur-sm"
                  >
                    <div className="flex items-center">
                      <Volume2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 animate-pulse" />
                      <span>{moduleInfo.coachName} is speaking...</span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Connection quality indicator (top right) */}
              <div className="absolute top-16 sm:top-20 right-4 z-10">
                <div
                  className={cn(
                    'flex items-center px-2 py-1 rounded-full text-xs',
                    'bg-black/40 backdrop-blur-sm',
                    isConnected ? 'text-green-400' : 'text-yellow-400',
                  )}
                >
                  <Shield className="h-3 w-3 mr-1 flex-shrink-0" />
                  <span className="hidden sm:inline">Connection: </span>
                  {isConnected ? 'Secure' : 'Connecting...'}
                </div>
              </div>

              {/* User video feed (bottom right corner) */}
              <AnimatePresence>
                {mode === 'video' && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    className="absolute bottom-28 sm:bottom-32 right-4 sm:right-6 z-20"
                  >
                    <div className="relative w-32 h-24 sm:w-48 sm:h-36 rounded-xl overflow-hidden border-2 border-white/20 shadow-2xl bg-gray-800">
                      {isVideoOn && userStream ? (
                        <>
                          <video
                            ref={(ref) => {
                              userVideoRef.current = ref;
                              // Assign stream immediately when ref is set
                              if (ref && userStream && !streamAssignedRef.current) {
                                ref.srcObject = userStream;
                                streamAssignedRef.current = true;
                                ref.play().catch(() => {});

                              }
                            }}
                            autoPlay
                            playsInline
                            muted
                            className="w-full h-full object-cover scale-x-[-1]"
                            onCanPlay={() => {
                              // Video is ready to play
                            }}
                            onError={(e) => {
                              console.error('[MediaCall] User video error:', e);
                            }}
                          />
                          {/* Video status indicator */}
                          <div className="absolute top-2 right-2">
                            <div className="bg-green-500 w-2 h-2 rounded-full shadow-lg"></div>
                          </div>
                          {/* Debug info */}
                          <div className="absolute bottom-1 left-1 text-xs text-white bg-black/50 px-1 rounded">
                            {userStream.getVideoTracks().length}V
                          </div>

                        </>
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-white bg-gray-700">
                          <VideoOff className="h-6 w-6 sm:h-8 sm:w-8 mb-1 text-gray-400" />
                          <span className="text-xs text-gray-400">
                            {!isVideoOn ? 'Camera Off' : 'No Stream'}
                          </span>
                          {/* Debug info */}
                          <div className="absolute bottom-1 left-1 text-xs text-white bg-black/50 px-1 rounded">
                            {isVideoOn ? 'ON' : 'OFF'} | {userStream ? 'S' : 'X'}
                          </div>
                        </div>
                      )}

                      {/* User video controls overlay */}
                      <div className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-200 bg-black/30 flex items-center justify-center">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={toggleVideo}
                          className="text-white hover:bg-white/20 p-2 rounded-full"
                        >
                          {isVideoOn ? (
                            <Video className="h-4 w-4" />
                          ) : (
                            <VideoOff className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Audio controls and voice status indicators */}
              <div className="absolute bottom-28 sm:bottom-32 left-4 sm:left-6 flex flex-col items-start">
                <AnimatePresence>
                  {/* Mic status indicator */}
                  {isMuted && (
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      className="bg-red-900/70 text-white text-xs py-1 px-2 rounded-full mb-2 backdrop-blur-sm border border-red-800/50 flex items-center"
                    >
                      <MicOff className="h-3 w-3 mr-1" />
                      <span>Muted</span>
                    </motion.div>
                  )}

                  {/* Voice recognition status indicators */}
                  {isUserSpeaking && (
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      className="bg-green-700/70 text-white text-xs py-1 px-2 rounded-full mb-2 backdrop-blur-sm flex items-center border border-green-600/50"
                    >
                      <div className="mr-1 h-2 w-2 rounded-full bg-white animate-ping"></div>
                      <span>Listening</span>
                    </motion.div>
                  )}

                  {/* Last heard message */}
                  {lastUserMessage && !isUserSpeaking && (
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      className="bg-blue-700/70 text-white text-xs py-1 px-2 rounded-full mb-2 backdrop-blur-sm border border-blue-600/50 max-w-[200px] sm:max-w-xs"
                    >
                      <div className="truncate">
                        <span className="opacity-75">Heard: </span>"
                        {lastUserMessage.length > 30
                          ? lastUserMessage.substring(0, 30) + '...'
                          : lastUserMessage}
                        "
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </>
        ) : !sessionEnded ? (
          // Enhanced Voice call view with modern design
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-lg mx-auto flex flex-col items-center justify-center h-full p-6 sm:p-8"
          >
            {/* Enhanced Coach avatar with modern styling */}
            <div className="relative w-32 h-32 sm:w-48 sm:h-48 mb-6 sm:mb-8">
              {/* Sophisticated animated rings for speaking effect */}
              {isAISpeaking && (
                <>
                  {/* Outer pulse ring */}
                  <motion.div
                    animate={{
                      scale: [1, 1.6],
                      opacity: [0.4, 0],
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: [0.25, 0.46, 0.45, 0.94],
                    }}
                    className="absolute inset-0 rounded-full bg-primary/20"
                  />
                  {/* Middle pulse ring */}
                  <motion.div
                    animate={{
                      scale: [1, 1.3],
                      opacity: [0.6, 0],
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      delay: 0.2,
                      ease: [0.25, 0.46, 0.45, 0.94],
                    }}
                    className="absolute inset-0 rounded-full bg-primary/30"
                  />
                  {/* Inner subtle glow */}
                  <motion.div
                    animate={{
                      opacity: [0.8, 1, 0.8],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                    className="absolute inset-0 rounded-full bg-gradient-to-r from-primary/10 to-primary/20 blur-sm"
                  />
                </>
              )}

              {/* Avatar container with enhanced effects */}
              <motion.div
                animate={{
                  scale: isAISpeaking ? [1, 1.01, 1] : 1,
                }}
                transition={{
                  duration: 2,
                  repeat: isAISpeaking ? Infinity : 0,
                  repeatType: 'reverse',
                  ease: 'easeInOut',
                }}
                className={cn(
                  'relative w-full h-full rounded-full z-10',
                  'flex items-center justify-center overflow-hidden',
                  'border-4 border-white bg-gray-100',
                  'transition-all duration-500',
                  isAISpeaking
                    ? 'shadow-2xl shadow-primary/30 ring-2 ring-primary/20'
                    : 'shadow-lg',
                )}
              >
                <img
                  src={coachAvatarImg}
                  alt={moduleInfo.coachName}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback if image doesn't load
                    (
                      e.target as HTMLImageElement
                    ).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                      moduleInfo.coachName,
                    )}&background=${moduleInfo.avatarColor.replace(
                      'bg-',
                      '',
                    )}&color=fff&size=200`;
                  }}
                />

                {/* Enhanced speaking indicator */}
                {isAISpeaking && (
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                    className="absolute -bottom-1 -right-1 w-6 h-6 sm:w-7 sm:h-7"
                  >
                    {/* Outer glow */}
                    <motion.div
                      animate={{
                        scale: [1, 1.4, 1],
                        opacity: [0.6, 0.2, 0.6],
                      }}
                      transition={{
                        duration: 1.2,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                      className="absolute inset-0 bg-primary/40 rounded-full blur-sm"
                    />
                    {/* Main indicator */}
                    <motion.div
                      animate={{
                        scale: [1, 1.1, 1],
                      }}
                      transition={{
                        duration: 0.8,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                      className="relative w-full h-full bg-primary rounded-full border-3 border-white shadow-lg flex items-center justify-center"
                    >
                      <motion.div
                        animate={{
                          opacity: [1, 0.5, 1],
                        }}
                        transition={{
                          duration: 1,
                          repeat: Infinity,
                          ease: 'easeInOut',
                        }}
                        className="w-2 h-2 bg-white rounded-full"
                      />
                    </motion.div>
                  </motion.div>
                )}
              </motion.div>
            </div>

            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-2"
            >
              {moduleInfo.coachName}
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-base sm:text-lg text-gray-600 mb-8 sm:mb-10 text-center font-medium"
            >
              AI Coach • {moduleInfo.title}
            </motion.p>

            {/* Enhanced AI speaking status */}
            <AnimatePresence mode="wait">
              {isAISpeaking ? (
                <motion.div
                  key="speaking"
                  initial={{ opacity: 0, y: 20, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -20, scale: 0.9 }}
                  className="text-center mb-8 sm:mb-10"
                >
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="inline-flex items-center px-6 py-3 bg-white/90 backdrop-blur-md rounded-full border border-gray-200/60 shadow-lg"
                  >
                    {/* Animated sound waves */}
                    <div className="flex items-center space-x-1 mr-3">
                      {[0, 1, 2].map((i) => (
                        <motion.div
                          key={i}
                          animate={{
                            scaleY: [0.3, 1, 0.3],
                            opacity: [0.6, 1, 0.6],
                          }}
                          transition={{
                            duration: 1,
                            repeat: Infinity,
                            delay: i * 0.1,
                            ease: 'easeInOut',
                          }}
                          className="w-1 h-4 bg-primary rounded-full"
                        />
                      ))}
                    </div>
                    <span className="text-sm text-gray-700 font-medium">
                      {moduleInfo.coachName} is speaking
                    </span>
                  </motion.div>
                </motion.div>
              ) : (
                <motion.div
                  key="waiting"
                  initial={{ opacity: 0, y: 20, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -20, scale: 0.9 }}
                  className="text-center mb-8 sm:mb-10"
                >
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="inline-flex items-center px-6 py-3 bg-white/90 backdrop-blur-md rounded-full border border-gray-200/60 shadow-lg"
                  >
                    {isUserSpeaking ? (
                      <>
                        {/* Listening pulse indicator */}
                        <motion.div
                          animate={{
                            scale: [1, 1.3, 1],
                            opacity: [0.6, 1, 0.6]
                          }}
                          transition={{
                            duration: 1.2,
                            repeat: Infinity,
                            ease: 'easeInOut'
                          }}
                          className="mr-3 w-3 h-3 rounded-full bg-primary"
                        />
                        <span className="text-sm text-gray-700 font-medium">
                          Listening...
                        </span>
                      </>
                    ) : (
                      <>
                        {/* Gentle mic indicator */}
                        <motion.div
                          animate={{
                            scale: [1, 1.05, 1],
                            opacity: [0.7, 1, 0.7]
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: 'easeInOut'
                          }}
                          className="mr-3"
                        >
                          <Mic className="h-4 w-4 text-gray-500" />
                        </motion.div>
                        <span className="text-sm text-gray-600">
                          Your turn to speak
                        </span>
                      </>
                    )}
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Enhanced last heard message */}
            {lastUserMessage && !isUserSpeaking && !isAISpeaking && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 px-5 py-3 bg-white rounded-xl border border-gray-200/70 shadow-sm max-w-sm backdrop-blur-sm"
              >
                <p className="text-sm text-gray-600">
                  <span className="font-semibold text-gray-800">
                    You said:{' '}
                  </span>
                  <span className="italic">
                    "
                    {lastUserMessage.length > 80
                      ? lastUserMessage.substring(0, 80) + '...'
                      : lastUserMessage}
                    "
                  </span>
                </p>

              </motion.div>
            )}

            {/* Turn-taking message - elegant feedback when microphone is blocked */}
            {turnTakingMessage && isMicrophoneBlocked && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                className="mb-6 px-5 py-3 bg-amber-50 border border-amber-200 rounded-xl shadow-sm max-w-sm backdrop-blur-sm"
              >
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    {/* Animated speaking indicator */}
                    {[0, 1, 2].map((i) => (
                      <motion.div
                        key={i}
                        className="w-1 h-3 bg-amber-500 rounded-full"
                        animate={{
                          scaleY: [0.3, 1, 0.3],
                          opacity: [0.6, 1, 0.6],
                        }}
                        transition={{
                          duration: 0.8,
                          repeat: Infinity,
                          delay: i * 0.1,
                        }}
                      />
                    ))}
                  </div>
                  <p className="text-sm text-amber-800 font-medium">
                    {turnTakingMessage}
                  </p>
                </div>
              </motion.div>
            )}
          </motion.div>
        ) : null}
      </div>

      {/* Enhanced Call controls - optimized for different devices */}
      {!sessionEnded && mode === 'video' ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-black/95 backdrop-blur-md px-4 sm:px-6 py-6 sm:py-8"
        >
          <div className="max-w-lg mx-auto flex justify-center items-center space-x-4 sm:space-x-6">
            {/* Enhanced Mute button */}
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                onClick={toggleMute}
                className={cn(
                  'rounded-full p-4 sm:p-5 transition-all duration-200 shadow-lg',
                  isMuted
                    ? 'bg-red-600 hover:bg-red-700 text-white shadow-red-500/30'
                    : 'bg-gray-700/90 hover:bg-gray-600 text-white shadow-gray-700/30',
                )}
                aria-label={isMuted ? 'Unmute microphone' : 'Mute microphone'}
              >
                {isMuted ? (
                  <MicOff className="h-5 w-5 sm:h-6 sm:w-6" />
                ) : (
                  <Mic className="h-5 w-5 sm:h-6 sm:w-6" />
                )}
              </Button>
            </motion.div>

            {/* Enhanced Video toggle button */}
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                onClick={toggleVideo}
                className={cn(
                  'rounded-full p-4 sm:p-5 transition-all duration-200 shadow-lg',
                  !isVideoOn
                    ? 'bg-red-600 hover:bg-red-700 text-white shadow-red-500/30'
                    : 'bg-gray-700/90 hover:bg-gray-600 text-white shadow-gray-700/30',
                )}
                aria-label={isVideoOn ? 'Turn off video' : 'Turn on video'}
              >
                {isVideoOn ? (
                  <Video className="h-5 w-5 sm:h-6 sm:w-6" />
                ) : (
                  <VideoOff className="h-5 w-5 sm:h-6 sm:w-6" />
                )}
              </Button>
            </motion.div>

            {/* Enhanced End call button */}
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                onClick={handleEndCall}
                className="rounded-full p-5 sm:p-6 bg-red-600 hover:bg-red-700 transition-all duration-200 text-white shadow-lg shadow-red-500/40"
                aria-label="End call"
              >
                <Phone className="h-6 w-6 sm:h-7 sm:w-7 transform rotate-135" />
              </Button>
            </motion.div>

            {/* Enhanced Settings button */}
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                className="rounded-full p-4 sm:p-5 bg-gray-700/90 hover:bg-gray-600 transition-all duration-200 text-white shadow-lg shadow-gray-700/30"
                aria-label="Call settings"
                onClick={() => setShowSettings(!showSettings)}
              >
                <Settings className="h-5 w-5 sm:h-6 sm:w-6" />
              </Button>
            </motion.div>
          </div>

          {/* Settings panel (collapsible) */}
          <AnimatePresence>
            {showSettings && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="mt-4 max-w-md mx-auto bg-gray-800 rounded-lg p-4 overflow-hidden"
              >
                <h3 className="text-white text-sm font-medium mb-3">
                  Call Settings
                </h3>

                {/* Volume control */}
                <div className="flex items-center mb-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setAudioVolume(0)}
                    className="p-1 text-gray-300 hover:text-white"
                  >
                    <VolumeX className="h-4 w-4" />
                  </Button>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={audioVolume}
                    onChange={(e) => setAudioVolume(parseInt(e.target.value))}
                    className="mx-2 flex-1 accent-primary"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setAudioVolume(100)}
                    className="p-1 text-gray-300 hover:text-white"
                  >
                    <Volume2 className="h-4 w-4" />
                  </Button>
                  <span className="text-xs text-white ml-2 w-8">
                    {audioVolume}%
                  </span>
                </div>

                {/* Connection info toggle */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowConnectionInfo(!showConnectionInfo)}
                  className="text-gray-300 hover:text-white text-xs w-full justify-start"
                >
                  <Info className="h-4 w-4 mr-2" />
                  {showConnectionInfo
                    ? 'Hide connection info'
                    : 'Show connection info'}
                </Button>

                {/* Connection details */}
                <AnimatePresence>
                  {showConnectionInfo && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="mt-2 text-xs text-gray-400 bg-gray-900/50 p-2 rounded"
                    >
                      <div className="flex justify-between mb-1">
                        <span>Session ID:</span>
                        <span className="font-mono">
                          {sessionId?.substring(0, 8) || 'Unknown'}
                        </span>
                      </div>
                      <div className="flex justify-between mb-1">
                        <span>Call duration:</span>
                        <span>{formatDuration(callDuration)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Status:</span>
                        <span
                          className={
                            isConnected ? 'text-green-400' : 'text-yellow-400'
                          }
                        >
                          {isConnected ? 'Connected' : 'Connecting...'}
                        </span>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      ) : !sessionEnded ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/90 backdrop-blur-md border-t border-gray-200/60 px-6 py-6 sm:p-8 sticky bottom-0 z-10 shadow-lg"
        >
          <div className="max-w-md mx-auto flex justify-center items-center gap-6 sm:gap-8">
            {/* Enhanced Mute button */}
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                onClick={toggleMute}
                variant="outline"
                className={cn(
                  'rounded-full h-14 w-14 sm:h-16 sm:w-16 p-0 transition-all duration-200 shadow-md',
                  isMuted
                    ? 'bg-red-50 border-red-300 text-red-600 hover:bg-red-100 shadow-red-200/50'
                    : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100 shadow-gray-200/50',
                )}
                aria-label={isMuted ? 'Unmute microphone' : 'Mute microphone'}
              >
                {isMuted ? (
                  <MicOff className="h-6 w-6 sm:h-7 sm:w-7" />
                ) : (
                  <Mic className="h-6 w-6 sm:h-7 sm:w-7" />
                )}
              </Button>
            </motion.div>

            {/* Enhanced End call button (larger) */}
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                onClick={handleEndCall}
                className="rounded-full h-16 w-16 sm:h-18 sm:w-18 p-0 bg-gradient-to-br from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 transition-all duration-200 text-white shadow-lg shadow-red-500/40"
                aria-label="End call"
              >
                <Phone className="h-7 w-7 sm:h-8 sm:w-8 transform rotate-135" />
              </Button>
            </motion.div>

            {/* Enhanced Speaker volume toggle */}
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                onClick={() => setAudioVolume(audioVolume === 0 ? 100 : 0)}
                variant="outline"
                className={cn(
                  'rounded-full h-14 w-14 sm:h-16 sm:w-16 p-0 transition-all duration-200 shadow-md',
                  audioVolume === 0
                    ? 'bg-gray-50 border-gray-300 text-gray-400 shadow-gray-200/50'
                    : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100 shadow-gray-200/50',
                )}
                aria-label={
                  audioVolume === 0 ? 'Unmute speaker' : 'Mute speaker'
                }
              >
                {audioVolume === 0 ? (
                  <VolumeX className="h-6 w-6 sm:h-7 sm:w-7" />
                ) : (
                  <Volume2 className="h-6 w-6 sm:h-7 sm:w-7" />
                )}
              </Button>
            </motion.div>
          </div>
        </motion.div>
      ) : null}
    </div>
  );
}
